import { useEffect, useMemo, useRef, useState } from "react";
import { Link } from "react-router-dom";
import api from "../lib/api";
import {
  Loader2, CheckCircle2, AlertTriangle, XCircle, ShieldAlert,
  Save, ExternalLink, Copy, FileText, Sparkles, Radar,
  ArrowUpCircle, Ban, Clock, BarChart3, History, Eye,
  RotateCcw, Archive, Trash2, ChevronDown, ChevronUp, Info, X,
} from "lucide-react";

// Stage → visible chip + label (Iter-51). The exact three lifecycle
// phrases the owner requested are stitched together from these.
const STAGE_CHIPS = {
  fast_queued: {
    cls:   "bg-charcoal-50 text-charcoal-600 border-black/10",
    Icon:  Clock,
    label: "Waiting",
  },
  fast_running: {
    cls:   "bg-blue-50 text-blue-700 border-blue-200",
    Icon:  Loader2,
    label: "Scanning",
    spin:  true,
  },
  // Iter-66 — after Iter-66, batches never auto-run deep audits, so
  // both fast_done stages are functionally identical for lifecycle
  // purposes ("this row is finalized, ready to view"). Labels reflect
  // that. `deep_audit_available` on the row indicates whether the
  // manual "Run Deep Audit" button should surface.
  fast_done_healthy: {
    cls:   "bg-emerald-50 text-emerald-800 border-emerald-200",
    Icon:  CheckCircle2,
    label: "Scan Complete",
    sub:   "Healthy across critical checks.",
  },
  fast_done_promising: {
    cls:   "bg-emerald-50 text-emerald-800 border-emerald-200",
    Icon:  CheckCircle2,
    label: "Scan Complete",
    sub:   "Outreach signals surfaced.",
  },
  // Iter-66 — deep_running only comes from the manual per-prospect
  // "Run Deep Audit" button. The bulk batch never produces this state.
  deep_running: {
    cls:   "bg-indigo-50 text-indigo-800 border-indigo-200",
    Icon:  Loader2,
    label: "Deep Audit Running",
    sub:   "Additional recommendations being analyzed.",
    spin:  true,
  },
  done: {
    cls:   "bg-emerald-100 text-emerald-900 border-emerald-300",
    Icon:  CheckCircle2,
    label: "Deep Audit Complete",
    sub:   "Full report available.",
  },
  failed: {
    cls:   "bg-red-50 text-red-700 border-red-200",
    Icon:  XCircle,
    label: "Failed",
  },
  cancelled: {
    cls:   "bg-charcoal-100 text-charcoal-600 border-black/10",
    Icon:  Ban,
    label: "Cancelled",
  },
};

const PRIORITY_STYLE = {
  critical: { label: "Critical", cls: "bg-red-50 text-red-800 border-red-200", Icon: XCircle, order: 0 },
  high:     { label: "High",     cls: "bg-amber-50 text-amber-800 border-amber-200", Icon: AlertTriangle, order: 1 },
  moderate: { label: "Moderate", cls: "bg-yellow-50 text-yellow-800 border-yellow-200", Icon: AlertTriangle, order: 2 },
  low:      { label: "Low",      cls: "bg-charcoal-50 text-charcoal-700 border-black/10", Icon: AlertTriangle, order: 3 },
  none:     { label: "No outreach claim", cls: "bg-emerald-50 text-emerald-800 border-emerald-200", Icon: CheckCircle2, order: 4 },
};

// Iter-67 — one coherent Prospect Scanner finding, coverage-aware.
// Iter-68 — adds explicit `moderate` tier for rows where the audit is
// healthy but the deterministic research module surfaced Moderate-tier
// signals (missing contact, weak CTA, stale content, donation buried).
// Tier vocabulary matches the owner's approved labels verbatim.
const FINDING_STYLE = {
  critical:                    { label: "Confirmed broken donation path",              cls: "bg-red-50 text-red-800 border-red-200",           Icon: XCircle },
  high:                        { label: "No donation path found after completed audit", cls: "bg-amber-50 text-amber-800 border-amber-200",     Icon: AlertTriangle },
  moderate:                    { label: "Moderate friction signals",                    cls: "bg-yellow-50 text-yellow-800 border-yellow-200",  Icon: AlertTriangle },
  limited:                     { label: "Limited audit — insufficient coverage",       cls: "bg-charcoal-50 text-charcoal-600 border-black/10", Icon: ShieldAlert },
  unverified:                  { label: "Verification blocked",                        cls: "bg-charcoal-50 text-charcoal-600 border-black/10", Icon: ShieldAlert },
  browser_reached_unresolved:  { label: "Browser reached site — donation flow still unverified", cls: "bg-blue-50 text-blue-800 border-blue-200",     Icon: ShieldAlert },
  browser_timeout:             { label: "Browser verification timed out",              cls: "bg-blue-50 text-blue-800 border-blue-200",         Icon: ShieldAlert },
  browser_error:               { label: "Browser verification could not complete",     cls: "bg-blue-50 text-blue-800 border-blue-200",         Icon: ShieldAlert },
  challenge_remained:          { label: "Site protection prevented verification",      cls: "bg-blue-50 text-blue-800 border-blue-200",         Icon: ShieldAlert },
  none:                        { label: "No broken donation paths found in completed audit", cls: "bg-emerald-50 text-emerald-800 border-emerald-200", Icon: CheckCircle2 },
  // Iter-71b — spam / hijack downgrade. Highest priority tier — a
  // row flagged as `downgraded_by_spam` renders this label regardless
  // of raw friction signals. Score is forced to 0 in the table and
  // the Outreach Email button is hidden.
  spam:                        { label: "Unverified — Possible spam or compromised domain", cls: "bg-charcoal-50 text-charcoal-600 border-black/10", Icon: ShieldAlert },
  // Iter-71d — post-Deep-Audit row summary. When the owner has
  // successfully run a Deep Audit for this row, the results table
  // stops rendering the empty fast-scan `donation_audit` and instead
  // shows the deep-audit outcome. `deep_failed` surfaces the reason
  // instead of pretending the row is still Limited.
  deep_complete:               { label: "Deep audit complete",                              cls: "bg-emerald-50 text-emerald-800 border-emerald-200", Icon: CheckCircle2 },
  deep_failed:                 { label: "Deep audit failed",                                cls: "bg-red-50 text-red-800 border-red-200",             Icon: XCircle },
  // Iter-71f — internal execution failure (process death / hit
  // retry cap). NEVER shown as Failed — a "Failed" chip would
  // imply the WEBSITE was at fault. The row is safe to retry;
  // the recovery cron will pick it up automatically.
  interrupted:                 { label: "Interrupted — will retry automatically",           cls: "bg-charcoal-50 text-charcoal-600 border-black/10", Icon: ShieldAlert },
};

// Iter-70 — map browser provenance / verification signals to a
// specific chip.  All of these remain Unverified (score 0, no
// outreach claim) — they only refine the display so the owner sees
// exactly WHY the row didn't produce a finding.
function _browserFallbackTier(audit, verification) {
  const provReason = audit?.browser_provenance?.reason || null;
  // Fast path — reconcile stamped the reason explicitly.
  if (provReason === "browser_timeout")             return "browser_timeout";
  if (provReason === "browser_error")               return "browser_error";
  if (provReason === "browser_no_paths_observed")   return "browser_reached_unresolved";
  if (provReason === "browser_render_succeeded")    return "browser_reached_unresolved";
  if (provReason === "challenge_remained")          return "challenge_remained";
  // Fallback path — browser_provenance missing (pre-fix data) but
  // report.browser_verification exists. Interpret it directly.
  if (verification) {
    const rc = verification.reason_code || "";
    const oc = verification.outcome     || "";
    if (rc === "row_wall_clock_45s")   return "browser_timeout";
    if (rc === "challenge_remained")   return "challenge_remained";
    if (rc === "browser_error"
          || oc === "browser_error")   return "browser_error";
    if (oc === "no_finding"
          || oc === "strong_opportunity")
                                          return "browser_reached_unresolved";
  }
  return null;
}

function deriveFinding(row) {
  const audit = row?.donation_audit;
  // Iter-71b — spam / hijack takes precedence over EVERY other tier.
  // Runs FIRST so a spam-flagged host can never render Moderate /
  // High / Critical / None regardless of raw friction signals.
  if (row?.downgraded_by_spam) {
    return { tier: "spam", label: FINDING_STYLE.spam.label,
             stored_priority: row?.priority || "none" };
  }
  // Iter-71f — internal execution failures (process death / retry
  // cap) render as Interrupted, not Failed. A row that hit its
  // internal retry cap is honestly "we couldn't finish scanning it",
  // NOT "the website is broken".
  const _INTERRUPTED = new Set([
    "interrupted_max_attempts",
    "never_attempted_bug",
    "reconciliation_attempted_stuck",
    "reconciliation_missing_completed_at",
  ]);
  if (row?.stage === "failed" && _INTERRUPTED.has(row?.fail_reason)) {
    return { tier: "interrupted",
             label: FINDING_STYLE.interrupted.label,
             stored_priority: row?.priority || "none" };
  }
  // Iter-71d — a completed Deep Audit REPLACES the row's finding
  // with the deep-audit outcome. Runs before the verdict-based /
  // zero-coverage logic because after Deep completes, the original
  // fast-scan `donation_audit` is empty (deep report overwrote it),
  // which would otherwise trip the zero-coverage guard → Limited.
  const das = row?.deep_audit_summary;
  if (das?.status === "complete") {
    return { tier: "deep_complete",
             label: FINDING_STYLE.deep_complete.label,
             stored_priority: row?.priority || "none" };
  }
  if (das?.status === "failed") {
    return { tier: "deep_failed",
             label: FINDING_STYLE.deep_failed.label,
             stored_priority: row?.priority || "none" };
  }
  const v = audit?.verdict;
  const p = row?.priority || "none";
  // Iter-70 — browser_provenance (or, as a fallback, the raw
  // browser_verification payload) tells us WHY a probe-blocked row
  // should get a different chip.  All fallback tiers remain
  // Unverified — no outreach claim, score 0.
  const browserTier = _browserFallbackTier(audit, row?.browser_verification);

  // ---- Natural tier from verdict (or legacy priority fallback) ----
  let tier, label;
  if (v === "donation_broken") {
    tier = "critical"; label = FINDING_STYLE.critical.label;
  } else if (v === "donation_missing") {
    tier = "high"; label = FINDING_STYLE.high.label;
  } else if (v === "donation_limited") {
    if (browserTier) { tier = browserTier; label = FINDING_STYLE[browserTier].label; }
    else { tier = "limited"; label = FINDING_STYLE.limited.label; }
  } else if (v === "donation_probe_blocked") {
    if (browserTier) { tier = browserTier; label = FINDING_STYLE[browserTier].label; }
    else { tier = "unverified"; label = FINDING_STYLE.unverified.label; }
  } else if (v === "donation_healthy") {
    if (p === "moderate") { tier = "moderate"; label = FINDING_STYLE.moderate.label; }
    else { tier = "none"; label = FINDING_STYLE.none.label; }
  } else {
    // Legacy row — no Iter-66/67 audit attached.
    const opp = row?.opportunity_score || 0;
    if (opp === 0 && (p === "critical" || p === "high")) {
      tier = "none"; label = "No actionable finding (legacy scan)";
    } else if (p === "critical") {
      tier = "critical"; label = "Confirmed critical outreach signal";
    } else if (p === "high") {
      tier = "high"; label = "Outreach opportunity";
    } else if (p === "moderate") {
      tier = "moderate"; label = FINDING_STYLE.moderate.label;
    } else {
      tier = "none"; label = "No actionable finding";
    }
  }

  // Iter-71 — Zero-coverage invariant. If the row's natural tier
  // would make a positive claim about a completed audit
  // (critical / high / moderate / none) but the crawler did not
  // inspect any pages (explicit 0 OR audit never ran), downgrade to
  // the browser-fallback tier when stamped, otherwise to `limited`.
  // Verdicts that are already honest about coverage
  // (donation_limited → limited, donation_probe_blocked → unverified,
  // and every browser-fallback tier) are left alone.
  const HEALTHY_CLAIM_TIERS = new Set(["critical", "high", "moderate", "none"]);
  const inspected = audit?.pages_inspected ?? audit?.pages_crawled;
  const zeroCov = (inspected == null || inspected === 0);
  if (zeroCov && HEALTHY_CLAIM_TIERS.has(tier)) {
    if (browserTier) {
      tier = browserTier; label = FINDING_STYLE[browserTier].label;
    } else {
      tier = "limited"; label = FINDING_STYLE.limited.label;
    }
  }

  return { tier, label, stored_priority: p };
}

function FindingChip({ finding }) {
  const s = FINDING_STYLE[finding.tier] || FINDING_STYLE.none;
  const Icon = s.Icon;
  return (
    <span
      data-testid={`finding-chip-${finding.tier}`}
      className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full border text-[10.5px] font-mono uppercase tracking-widest ${s.cls}`}
    >
      <Icon className="w-3 h-3" /> {finding.label}
    </span>
  );
}

function priorityChip(p) {
  const s = PRIORITY_STYLE[p || "none"];
  return (
    <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full border text-[10.5px] font-mono uppercase tracking-widest ${s.cls}`}>
      <s.Icon className="w-3 h-3" /> {s.label}
    </span>
  );
}

function StageChip({ stage }) {
  const s = STAGE_CHIPS[stage] || STAGE_CHIPS.fast_queued;
  const Icon = s.Icon;
  return (
    <span
      data-testid={`stage-chip-${stage}`}
      className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full border text-[10.5px] font-mono uppercase tracking-widest ${s.cls}`}
    >
      <Icon className={`w-3 h-3 ${s.spin ? "animate-spin" : ""}`} />
      {s.label}
    </span>
  );
}

function StageStatusLine({ row }) {
  // The three prescribed lifecycle phrases, rendered as the primary
  // narrative under the row.
  const s = STAGE_CHIPS[row.stage] || {};
  if (!s.sub && row.stage !== "failed" && row.stage !== "cancelled") return null;
  if (row.stage === "failed") {
    const err = row.fast_error || row.deep_error || "Scan failed";
    return <span className="text-[11px] text-red-600">{err}</span>;
  }
  if (row.stage === "cancelled") {
    return <span className="text-[11px] text-charcoal-500">Cancelled before scan started.</span>;
  }
  return <span className="text-[11px] text-charcoal-500">{s.sub}</span>;
}

function formatETA(sec) {
  if (!sec || sec <= 0) return "—";
  if (sec < 60) return `~${sec}s`;
  const m = Math.round(sec / 60);
  return `~${m} min`;
}

// Iter-54 — short "N days ago / today / yesterday" formatter for the
// history table's Started column and the duplicate-awareness chip.
function relativeDate(iso) {
  if (!iso) return "—";
  try {
    const then = new Date(iso).getTime();
    const now = Date.now();
    const diffMin = Math.floor((now - then) / 60000);
    if (diffMin < 1)   return "just now";
    if (diffMin < 60)  return `${diffMin} min ago`;
    const diffH = Math.floor(diffMin / 60);
    if (diffH < 24)    return `${diffH}h ago`;
    const diffD = Math.floor(diffH / 24);
    if (diffD === 1)   return "yesterday";
    if (diffD < 14)    return `${diffD} days ago`;
    return new Date(iso).toLocaleDateString(undefined,
      { month: "short", day: "numeric" });
  } catch (_) { return "—"; }
}

function fullDate(iso) {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleString(undefined, {
      month: "short", day: "numeric", year: "numeric",
      hour: "numeric", minute: "2-digit",
    });
  } catch (_) { return "—"; }
}

// Iter-54 — batch status → chip metadata for the history table.
// Iter-59 — adds `terminated` (recovery-terminated batch, no successes).
const BATCH_STATUS_META = {
  running: {
    cls:   "bg-blue-50 text-blue-800 border-blue-200",
    label: "Running", Icon: Loader2, spin: true,
  },
  completed: {
    cls:   "bg-emerald-50 text-emerald-800 border-emerald-200",
    label: "Completed", Icon: CheckCircle2,
  },
  completed_with_failures: {
    cls:   "bg-amber-50 text-amber-800 border-amber-200",
    label: "Completed (partial)", Icon: AlertTriangle,
  },
  cancelled: {
    cls:   "bg-charcoal-100 text-charcoal-600 border-black/10",
    label: "Cancelled", Icon: Ban,
  },
  terminated: {
    cls:   "bg-red-50 text-red-700 border-red-200",
    label: "Terminated", Icon: Ban,
  },
};

function BatchStatusChip({ status }) {
  const s = BATCH_STATUS_META[status] || BATCH_STATUS_META.completed;
  const Icon = s.Icon;
  return (
    <span
      data-testid={`batch-status-${status}`}
      className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full border text-[10.5px] font-mono uppercase tracking-widest ${s.cls}`}
    >
      <Icon className={`w-3 h-3 ${s.spin ? "animate-spin" : ""}`} />
      {s.label}
    </span>
  );
}

export default function ProspectScan() {
  const [websitesText, setWebsitesText] = useState("");
  const [notes, setNotes] = useState("");
  const [label, setLabel] = useState("");
  // Iter-66 — `scanMode` state removed. Bulk batches always run the
  // bounded donation-path audit; there is no auto-deep mode.
  const [batchId, setBatchId] = useState(null);
  const [batchMeta, setBatchMeta] = useState(null);        // Iter-54 — label, started_at, is_historical, status
  const [rows, setRows] = useState([]);
  const [stats, setStats] = useState(null);
  // Iter-69 — recommended shortlist (server-computed).
  const [shortlist, setShortlist] = useState(null);           // {shortlist,needs_manual_review,possible_only,totals} | null
  const [shortlistLoading, setShortlistLoading] = useState(false);
  const [showPossibleStrip, setShowPossibleStrip] = useState(false);   // collapsed by default per spec
  const [running, setRunning] = useState(false);
  const [error, setError] = useState(null);
  const [expanded, setExpanded] = useState(null);
  const [detailFor, setDetailFor] = useState(null);   // Iter-68 — Detail panel open state (scanId | null)
  const [draftFor, setDraftFor] = useState(null);
  const [draftText, setDraftText] = useState("");
  const [draftSource, setDraftSource] = useState("");
  const [draftAiUsed, setDraftAiUsed] = useState(false);  // Iter-68
  const [draftLoading, setDraftLoading] = useState(false);
  const [sortKey, setSortKey] = useState("opportunity");
  const [stageFilter, setStageFilter] = useState("all");   // all | fast_done | deep_done
  const [tierFilter, setTierFilter] = useState("all");     // Iter-71 — all | critical | high | moderate | limited | unverified | none
  // Iter-54 — history, active-batch auto-restore, duplicate-awareness.
  const [batchHistory, setBatchHistory] = useState([]);
  const [historyLoading, setHistoryLoading] = useState(false);
  const [duplicates, setDuplicates] = useState({});   // { host → { last_scanned_at, times_seen } }
  // Iter-66 — batch-delete confirmation modal state.
  const [deleteTarget, setDeleteTarget] = useState(null);  // {batch_id, label, total} | null
  const [deleteBusy, setDeleteBusy] = useState(false);
  const pollRef = useRef(null);
  const dupTimerRef = useRef(null);

  useEffect(() => () => {
    if (pollRef.current) clearInterval(pollRef.current);
    if (dupTimerRef.current) clearTimeout(dupTimerRef.current);
  }, []);

  // Iter-54 — on mount, do two things in parallel:
  //   1. Load the batch history (source-of-truth for the History table)
  //   2. Detect an active batch and resume polling if found
  // Both are idempotent; a refresh mid-batch never starts a duplicate.
  useEffect(() => {
    (async () => {
      try {
        const active = await api.get("/admin/prospect-scans/active-batch");
        if (active.data.batch_id) {
          setBatchId(active.data.batch_id);
          setRunning(true);
          pollRef.current = setInterval(
            () => poll(active.data.batch_id), 3000);
          poll(active.data.batch_id);
        }
      } catch (_) { /* soft-fail */ }
      loadBatchHistory();
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Iter-54 — refresh history whenever a batch transitions to done.
  useEffect(() => {
    if (!running && batchId && stats &&
        (stats.scanning_now || 0) === 0 && (stats.waiting || 0) === 0) {
      loadBatchHistory();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [running, batchId]);

  // Iter-54 — duplicate-awareness debounce. Only fires when the paste
  // input has 3+ chars and the user has paused for 500ms.
  useEffect(() => {
    if (dupTimerRef.current) clearTimeout(dupTimerRef.current);
    if (!websitesText || websitesText.trim().length < 3) {
      setDuplicates({});
      return;
    }
    dupTimerRef.current = setTimeout(async () => {
      try {
        const r = await api.post(
          "/admin/prospect-scans/check-existing",
          { websites_text: websitesText });
        setDuplicates(r.data.existing || {});
      } catch (_) { /* soft-fail */ }
    }, 500);
    return () => { if (dupTimerRef.current) clearTimeout(dupTimerRef.current); };
  }, [websitesText]);

  async function loadBatchHistory() {
    setHistoryLoading(true);
    try {
      const r = await api.get("/admin/prospect-scans/batches?limit=50");
      setBatchHistory(r.data.batches || []);
    } catch (_) { /* soft-fail */ }
    finally { setHistoryLoading(false); }
  }

  // Iter-69/70 — pull the recommended shortlist for the current batch.
  // While `phase === "browser_verification"` we poll every 3s so the
  // progress card stays live. Stops as soon as the phase is terminal.
  async function loadShortlist(bid) {
    if (!bid) { setShortlist(null); return; }
    setShortlistLoading(true);
    try {
      const r = await api.get(
        `/admin/prospect-scans/batches/${bid}/shortlist`);
      setShortlist(r.data || null);
      if (r.data && r.data.phase === "browser_verification") {
        // Schedule the next poll; keep referencing bid so a batch change
        // cancels the loop naturally via the phase check.
        setTimeout(() => loadShortlist(bid), 3000);
      }
    } catch (_) { setShortlist(null); }
    finally { setShortlistLoading(false); }
  }

  async function poll(bid) {
    try {
      const r = await api.get(
        `/admin/prospect-scans?batch_id=${bid}&sort=${sortKey}&limit=200`);
      setRows(r.data.scans || []);
      setStats(r.data.batch_stats || null);
      setBatchMeta(r.data.batch_meta || null);
      const st = r.data.batch_stats || {};
      // Batch is done when nothing is queued or running.
      const idle = (st.scanning_now || 0) === 0 && (st.waiting || 0) === 0;
      if (idle) {
        clearInterval(pollRef.current); pollRef.current = null;
        setRunning(false);
        // Iter-69 — recompute shortlist once the batch finishes.
        loadShortlist(bid);
      }
    } catch (_) { /* keep polling */ }
  }

  async function openHistoricalBatch(bid) {
    // Load a completed batch's saved results — NO rescan, per spec.
    if (pollRef.current) { clearInterval(pollRef.current); pollRef.current = null; }
    setError(null);
    setBatchId(bid);
    setRows([]); setStats(null); setBatchMeta(null); setRunning(false);
    setShortlist(null);   // Iter-69
    try {
      const r = await api.get(
        `/admin/prospect-scans?batch_id=${bid}&sort=${sortKey}&limit=200`);
      setRows(r.data.scans || []);
      setStats(r.data.batch_stats || null);
      setBatchMeta(r.data.batch_meta || null);
      // If for any reason the historical batch is somehow still running,
      // resume polling. Otherwise leave it static.
      const st = r.data.batch_stats || {};
      const stillRunning = (st.scanning_now || 0) > 0 || (st.waiting || 0) > 0;
      if (stillRunning) {
        setRunning(true);
        pollRef.current = setInterval(() => poll(bid), 3000);
      } else {
        // Iter-69 — historical, already terminal → load shortlist.
        loadShortlist(bid);
      }
      // Scroll results into view.
      setTimeout(() => {
        const el = document.querySelector('[data-testid="prospect-results-table"]');
        if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
      }, 100);
    } catch (err) {
      setError("Could not load that batch. It may have been deleted.");
    }
  }

  // Iter-56c → Iter-66 — Delete a historical batch. Owner-only endpoint
  // enforces ownership + refuses if the batch is still actively
  // running. If the deleted batch is the one currently on-screen, we
  // clear the results panel so the UI never renders stale scan rows
  // against a now-missing batch.
  //
  // Iter-66 — confirmation is now a proper modal (was `window.confirm`).
  // `deleteTarget` holds `{batch_id, label, total}` while the modal
  // is open. `confirmDeleteBatch` runs the actual DELETE call.
  function askDeleteBatch(bid, label, total) {
    setDeleteTarget({batch_id: bid, label: label || "", total: total || 0});
  }

  async function confirmDeleteBatch() {
    const target = deleteTarget;
    if (!target) return;
    setDeleteBusy(true);
    try {
      const r = await api.delete(
        `/admin/prospect-scans/batch/${target.batch_id}`);
      // If the currently open batch was the deleted one, wipe the view.
      if (batchId === target.batch_id) {
        if (pollRef.current) { clearInterval(pollRef.current); pollRef.current = null; }
        setBatchId(null); setRows([]); setStats(null);
        setBatchMeta(null); setRunning(false);
      }
      loadBatchHistory();
      const preserved = r?.data?.preserved_leads || 0;
      const preservedLabel = preserved > 0
        ? ` — ${preserved} lead${preserved === 1 ? "" : "s"} preserved in the CRM`
        : "";
      setError(`Batch deleted.${preservedLabel}`);
      setDeleteTarget(null);
    } catch (err) {
      setError(err?.response?.data?.detail || "Delete failed.");
    } finally {
      setDeleteBusy(false);
    }
  }

  async function startBatch(e) {
    e.preventDefault();
    if (!websitesText.trim()) return;
    if (running && batchId) {
      setError("A batch is already running. Wait for it to finish, "
                + "or click Cancel Remaining first.");
      return;
    }
    setError(null); setRunning(true); setRows([]); setStats(null);
    setBatchId(null); setBatchMeta(null);
    try {
      const r = await api.post("/admin/prospect-scans/batch", {
        websites_text: websitesText,
        notes: notes.trim() || null,
        // Iter-66 — scan_mode is hardcoded to "prospect". The old
        // "full" mode (deep-audit every org) is defunct — batches
        // never auto-run deep audits.
        scan_mode: "prospect",
        label: label.trim() || null,
      });
      setBatchId(r.data.batch_id);
      pollRef.current = setInterval(() => poll(r.data.batch_id), 3000);
      poll(r.data.batch_id);
      setWebsitesText("");
      setDuplicates({});
    } catch (err) {
      const detail = err?.response?.data?.detail || "Failed to start batch.";
      // Iter-55b — if we hit the "batch already running" rate-limit
      // but the /active-batch endpoint returned nothing on mount,
      // the owner is stuck. Surface the recovery escape hatch.
      const isRateLimit = err?.response?.status === 429;
      const isStuckPattern = isRateLimit && !batchId;
      if (isStuckPattern) {
        setError(detail + " (If this looks stuck, try 'Recover stuck batches' below.)");
      } else {
        setError(detail);
      }
      setRunning(false);
    }
  }

  async function recoverStuck() {
    // Iter-55b — user-visible escape hatch when a prior batch worker
    // crashed mid-run and left rows in limbo (stage=fast_done_promising
    // with status=running, or *_running rows untouched >15min).
    if (!window.confirm("Force-recover any stuck batches? Their fast-scan "
       + "results are preserved; only the deep audit is skipped for the "
       + "affected rows.")) return;
    try {
      const r = await api.post("/admin/prospect-scans/recover-stuck", {});
      const n = r.data.unstuck_count || 0;
      setError(n > 0
        ? `Recovered ${n} stuck row${n === 1 ? "" : "s"}. You can start a new batch now.`
        : "Nothing to recover — no stuck rows found.");
      // Refresh history + active-batch after recovery.
      loadBatchHistory();
      const active = await api.get("/admin/prospect-scans/active-batch");
      if (active.data.batch_id) {
        setBatchId(active.data.batch_id);
        setRunning(true);
        if (pollRef.current) clearInterval(pollRef.current);
        pollRef.current = setInterval(() => poll(active.data.batch_id), 3000);
        poll(active.data.batch_id);
      } else {
        setBatchId(null); setRunning(false);
      }
    } catch (err) {
      setError(err?.response?.data?.detail || "Recovery failed.");
    }
  }

  async function saveAsLead(scanId) {
    try {
      await api.post(
        `/admin/prospect-scans/${scanId}/save-as-lead`, {});
      if (batchId) poll(batchId);
    } catch (err) {
      setError(err?.response?.data?.detail || "Save-as-lead failed.");
    }
  }

  async function requestDeep(scanId) {
    try {
      await api.post(
        `/admin/prospect-scans/${scanId}/deep-scan`, {});
      if (batchId) poll(batchId);
    } catch (err) {
      setError(err?.response?.data?.detail || "Deep-audit request failed.");
    }
  }

  async function cancelBatch() {
    if (!batchId) return;
    if (!window.confirm(
      "Terminate this batch?\n\n"
      + "This immediately stops every queued and in-flight scan and "
      + "unlocks the scanner so you can start a new batch right away. "
      + "Any results from workers that finish after this point are "
      + "discarded.\n\n"
      + "This cannot be undone."
    )) return;
    try {
      await api.post(
        `/admin/prospect-scans/batch/${batchId}/cancel`, {});
      // Iter-58 — terminate flips every row out of active stages, so
      // the batch is done as far as the UI is concerned. Clear local
      // polling state immediately; don't wait for the poll to notice.
      if (pollRef.current) { clearInterval(pollRef.current); pollRef.current = null; }
      setBatchId(null); setRows([]); setStats(null);
      setBatchMeta(null); setRunning(false);
      loadBatchHistory();
      setError("Batch terminated. You can start a new one below.");
    } catch (err) {
      setError(err?.response?.data?.detail || "Terminate failed.");
    }
  }

  async function generateDraft(scanId) {
    setDraftFor(scanId); setDraftText(""); setDraftSource(""); setDraftAiUsed(false); setDraftLoading(true);
    try {
      const r = await api.post(
        `/admin/prospect-scans/${scanId}/outreach-draft`, {});
      setDraftText(r.data.draft || "");
      setDraftSource(r.data.source || "");
      setDraftAiUsed(!!r.data.ai_used);
    } catch (err) {
      // Iter-69 — 409 = row is Unverified / spam / Possible-only; the
      // endpoint refused to draft. Surface the exact refusal reason.
      const status = err?.response?.status;
      const detail = err?.response?.data?.detail
                      || (status === 409
                          ? "No verified finding on this row — outreach draft is disabled."
                          : "Draft generation failed.");
      setDraftText(detail);
      setDraftSource(status === 409 ? "refused" : "error");
      setDraftAiUsed(false);
    } finally { setDraftLoading(false); }
  }

  useEffect(() => {
    if (batchId) poll(batchId);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sortKey]);

  const filteredRows = rows.filter((r) => {
    if (stageFilter === "fast_done") {
      if (!["fast_done_healthy", "fast_done_promising",
             "deep_running", "done"].includes(r.stage)) return false;
    } else if (stageFilter === "deep_done") {
      if (r.stage !== "done") return false;
    }
    // Iter-71 — tier filter uses the display-layer `deriveFinding()`
    // so 0-coverage rows correctly filter as Limited (not None).
    // Iter-71h — also let "deep-audit interrupted" rows through so
    // the tier filter doesn't hide them (they still have a real
    // fast-scan finding to classify).
    if (tierFilter !== "all") {
      const hasFastResult = !!(r?.donation_audit || r?.prospect_research);
      const isDeepInterrupted =
        (r.stage === "terminated" && hasFastResult) ||
        (r.stage === "failed"     && hasFastResult && !!r.deep_error) ||
        (r.stage === "fast_done_promising" && !!r.deep_error);
      const canOpen = ["fast_done_healthy", "fast_done_promising",
                       "deep_running", "done"].includes(r.stage) ||
                      isDeepInterrupted;
      if (!canOpen) return false;
      const t = deriveFinding(r).tier;
      // Group browser fallback tiers under "unverified" for the filter
      // (they're all coverage-blocked variants; the chip itself still
      // renders the specific label).
      const bucket = ["browser_timeout", "browser_error",
                       "browser_reached_unresolved",
                       "challenge_remained", "spam",
                       "deep_failed"].includes(t) ? "unverified"
                    : (t === "deep_complete") ? "none"
                    : t;
      if (bucket !== tierFilter) return false;
    }
    return true;
  });

  // Iter-71 — tier bucket counts for the filter chips. Uses the same
  // `deriveFinding` output as the row rendering so the numbers match
  // exactly what the user sees in the table.
  const tierCounts = { critical: 0, high: 0, moderate: 0, limited: 0,
                        unverified: 0, none: 0 };
  for (const r of rows) {
    if (!["fast_done_healthy", "fast_done_promising",
           "deep_running", "done"].includes(r.stage)) continue;
    const t = deriveFinding(r).tier;
    const bucket = ["browser_timeout", "browser_error",
                     "browser_reached_unresolved",
                     "challenge_remained", "spam",
                     "deep_failed"].includes(t) ? "unverified"
                  : (t === "deep_complete") ? "none"
                  : t;
    if (bucket in tierCounts) tierCounts[bucket] += 1;
  }

  const criticalCount = rows.filter(r => r.priority === "critical").length;
  const highCount     = rows.filter(r => r.priority === "high").length;
  const skippedCount  = rows.filter(r => r.stage === "fast_done_healthy").length;

  return (
    <div className="min-h-screen bg-parchment">
      <div className="max-w-7xl mx-auto px-6 py-8">
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-start justify-between gap-4">
            <div>
              <p className="text-[10px] font-mono uppercase tracking-widest text-charcoal-400">Owner dashboard · outreach research</p>
              <h1 className="mt-1 text-3xl font-semibold tracking-tighter text-charcoal-900">Prospect Scanner</h1>
              <p className="text-sm text-charcoal-500 mt-1 max-w-2xl">
                Paste 25–100 nonprofit websites. Mission Watch runs a bounded
                donation-path audit on every URL (~15–45s each, 4 in
                parallel). Every row lands as a terminal result you can act
                on — deep audits are a manual, per-prospect action.
              </p>
            </div>
            <Link to="/admin/cost" data-testid="prospect-back"
                  className="text-xs font-mono uppercase tracking-widest text-charcoal-500 hover:text-charcoal-900 whitespace-nowrap">
              ← Owner Dashboard
            </Link>
          </div>
        </div>

        {/* Iter-54 — Prospect Scan History */}
        <ProspectScanHistory
          batches={batchHistory}
          loading={historyLoading}
          onOpen={openHistoricalBatch}
          onDelete={askDeleteBatch}
          currentBatchId={batchId}
        />

        {/* Batch input */}
        <form onSubmit={startBatch} data-testid="prospect-batch-form"
              className="rounded-2xl bg-white border border-black/5 shadow-soft p-6 mb-6">
          <label className="block text-[11px] font-mono uppercase tracking-widest text-charcoal-500 mb-1">
            Websites — one per line (max 100)
          </label>
          <textarea value={websitesText} onChange={(e) => setWebsitesText(e.target.value)}
                    rows={8}
                    data-testid="prospect-batch-input"
                    placeholder={"charitywater.org\nlocalfoodbank.org\nhopehouse.org"}
                    className="w-full px-3 py-2 rounded-lg border border-black/10 text-sm font-mono focus:outline-none focus:border-charcoal-400" />

          {/* Iter-54 — duplicate-awareness chips */}
          {Object.keys(duplicates).length > 0 && (
            <div className="mt-2 flex flex-wrap gap-1.5" data-testid="prospect-duplicate-chips">
              {Object.entries(duplicates).slice(0, 12).map(([host, info]) => (
                <span key={host}
                      className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full border border-amber-200 bg-amber-50 text-[10.5px] font-mono text-amber-800"
                      title={`Last scanned ${fullDate(info.last_scanned_at)} · ${info.times_seen} previous scan${info.times_seen !== 1 ? "s" : ""}`}>
                  <Archive className="w-3 h-3" />
                  {host} · last scanned {relativeDate(info.last_scanned_at)}
                </span>
              ))}
              {Object.keys(duplicates).length > 12 && (
                <span className="text-[10.5px] text-charcoal-400 font-mono">
                  + {Object.keys(duplicates).length - 12} more previously scanned
                </span>
              )}
            </div>
          )}
          {/* Iter-66 — scan-mode picker REMOVED. The bulk batch always
              runs the bounded donation-path audit; there is no
              auto-deep mode. Deep Audit is a manual per-prospect
              action via the "Run Deep Audit" button on each row. */}

          <div className="mt-4 grid grid-cols-1 md:grid-cols-3 gap-3">
            <input value={label} onChange={(e) => setLabel(e.target.value)}
                   placeholder={websitesText.trim()
                     ? `Batch name (default: "${(websitesText.split(/\s+/).filter(x => x.trim().length >= 3).length)}-site batch")`
                     : 'Batch name (optional)'}
                   data-testid="prospect-batch-label"
                   maxLength={140}
                   className="px-3 py-2 rounded-lg border border-black/10 text-sm focus:outline-none focus:border-charcoal-400" />
            <input value={notes} onChange={(e) => setNotes(e.target.value)}
                   placeholder="Optional shared notes (e.g. 'referred from event')"
                   data-testid="prospect-batch-notes"
                   className="px-3 py-2 rounded-lg border border-black/10 text-sm focus:outline-none focus:border-charcoal-400" />
            <button type="submit" disabled={running || !websitesText.trim()}
                    data-testid="prospect-batch-run"
                    className="inline-flex items-center justify-center gap-2 px-4 py-2 rounded-full bg-charcoal-900 text-white text-xs font-mono uppercase tracking-widest disabled:opacity-50">
              {running ? <><Loader2 className="w-3.5 h-3.5 animate-spin" /> Scanning… ({stats?.completed || 0}/{stats?.total || 0})</>
                       : <>Scan Prospects</>}
            </button>
          </div>
          <p className="mt-3 text-xs text-charcoal-400">
            Owner-only. Rate-limited (1 batch at a time · 500 scans/hr). Runs up to 4 scans in parallel.
            No email is ever sent to the target organizations.
          </p>
          {error && (
            <div className="mt-3 space-y-2">
              <p className="text-xs text-red-600" data-testid="prospect-error">{error}</p>
              {/* Iter-55b — recovery escape hatch. Always available so the
                  owner can never be locked out by a stuck batch worker. */}
              <button type="button" onClick={recoverStuck}
                      data-testid="prospect-recover-stuck"
                      className="inline-flex items-center gap-1 px-3 py-1 rounded-full border border-black/10 bg-white text-[10.5px] font-mono uppercase tracking-widest text-charcoal-700 hover:border-black/20">
                <RotateCcw className="w-3 h-3" />
                Recover stuck batches
              </button>
            </div>
          )}
        </form>

        {/* Batch counter strip */}
        {stats && (
          <div className="grid grid-cols-2 md:grid-cols-5 gap-3 mb-4" data-testid="prospect-batch-strip">
            <div className="rounded-xl bg-white border border-black/5 shadow-soft p-4">
              <p className="text-[10px] font-mono uppercase tracking-widest text-blue-700">Scanning now</p>
              <p className="text-2xl font-semibold tracking-tight text-charcoal-900 mt-1"
                 data-testid="stat-scanning-now">{stats.scanning_now}</p>
            </div>
            <div className="rounded-xl bg-white border border-black/5 shadow-soft p-4">
              <p className="text-[10px] font-mono uppercase tracking-widest text-charcoal-500">Waiting</p>
              <p className="text-2xl font-semibold tracking-tight text-charcoal-900 mt-1"
                 data-testid="stat-waiting">{stats.waiting}</p>
            </div>
            <div className="rounded-xl bg-white border border-black/5 shadow-soft p-4">
              <p className="text-[10px] font-mono uppercase tracking-widest text-emerald-700">Completed</p>
              <p className="text-2xl font-semibold tracking-tight text-charcoal-900 mt-1"
                 data-testid="stat-completed">{stats.completed}<span className="text-charcoal-400 text-base">/{stats.total}</span></p>
            </div>
            <div className="rounded-xl bg-white border border-black/5 shadow-soft p-4">
              <p className="text-[10px] font-mono uppercase tracking-widest text-red-700">Failed</p>
              <p className="text-2xl font-semibold tracking-tight text-charcoal-900 mt-1"
                 data-testid="stat-failed">{(stats.failed || 0) + (stats.terminated || 0)}</p>
            </div>
            <div className="rounded-xl bg-white border border-black/5 shadow-soft p-4">
              <p className="text-[10px] font-mono uppercase tracking-widest text-charcoal-500">Est. remaining</p>
              <p className="text-2xl font-semibold tracking-tight text-charcoal-900 mt-1"
                 data-testid="stat-eta">{formatETA(stats.eta_seconds)}</p>
            </div>
          </div>
        )}

        {/* Iter-61 — Incomplete-batch safeguard.
            The reconciliation invariant is
              terminal_count = completed + failed + cancelled + terminated
            and unprocessed = total - terminal_count. But `unprocessed`
            counts BOTH mid-flight rows (queued/running/deep_running)
            AND stragglers reconciliation later force-closes.

            While a batch is still running, `unprocessed > 0` is the
            NORMAL state (URLs waiting to be pulled). Showing a
            "worker crashed" banner then is false and alarming.

            Correct condition: only when the batch has reached a
            terminal state — no rows scanning, none waiting — and yet
            `unprocessed > 0` (rows in a non-terminal stage that the
            reconciliation sweep hasn't yet landed OR a manual data
            edge case). While active, we render a calm progress line
            instead. */}
        {stats && (() => {
          const isActive = (stats.scanning_now || 0) > 0
                            || (stats.waiting || 0) > 0;
          const terminalUnprocessed = (stats.unprocessed || 0) > 0 && !isActive;
          if (isActive) {
            const processed = (stats.terminal_count || 0);
            const remaining = Math.max(0, (stats.total || 0) - processed);
            return (
              <div className="rounded-2xl border border-blue-200 bg-blue-50 p-3 mb-4 flex items-center gap-3"
                   data-testid="prospect-batch-progress-banner">
                <Loader2 className="w-4 h-4 text-blue-700 shrink-0 animate-spin" />
                <p className="text-sm text-blue-900">
                  <span className="font-semibold">{processed}</span> of{" "}
                  <span className="font-semibold">{stats.total}</span> processed —{" "}
                  <span className="font-semibold">{remaining}</span> remaining
                </p>
              </div>
            );
          }
          if (terminalUnprocessed) {
            return (
              <div className="rounded-2xl border border-amber-300 bg-amber-50 p-4 mb-4 flex items-start gap-3"
                   data-testid="prospect-batch-incomplete-banner">
                <AlertTriangle className="w-5 h-5 text-amber-700 shrink-0 mt-0.5" />
                <div className="flex-1">
                  <p className="text-sm font-semibold text-amber-900">
                    Incomplete — {stats.unprocessed} site{stats.unprocessed === 1 ? "" : "s"} unprocessed
                  </p>
                  <p className="text-xs text-amber-800 mt-0.5">
                    This batch has {stats.terminal_count || 0} of {stats.total} URLs accounted for.
                    The remaining {stats.unprocessed} did not reach a terminal state. This usually means
                    the reconciliation sweep hasn't finished yet — refresh in a moment.
                  </p>
                </div>
              </div>
            );
          }
          return null;
        })()}
        {stats
                && (stats.scanning_now || 0) === 0
                && (stats.waiting || 0) === 0
                && (stats.unprocessed || 0) === 0
                && (stats.terminated || 0) > 0
                && (stats.completed || 0) === 0 && (
          <div className="rounded-2xl border border-red-200 bg-red-50 p-4 mb-4 flex items-start gap-3"
               data-testid="prospect-batch-terminated-banner">
            <Ban className="w-5 h-5 text-red-700 shrink-0 mt-0.5" />
            <div className="flex-1">
              <p className="text-sm font-semibold text-red-900">
                Batch terminated — {stats.terminated} URL{stats.terminated === 1 ? "" : "s"} did not complete
              </p>
              <p className="text-xs text-red-800 mt-0.5">
                No successful scans in this batch. Start a fresh batch below.
              </p>
            </div>
          </div>
        )}

        {/* Iter-54 — Historical banner (when viewing saved results) */}
        {batchMeta && batchMeta.is_historical && rows.length > 0 && (
          <div className="rounded-2xl border border-blue-200 bg-blue-50 p-4 mb-4 flex items-start gap-3"
               data-testid="prospect-historical-banner">
            <Eye className="w-5 h-5 text-blue-700 shrink-0 mt-0.5" />
            <div className="flex-1">
              <p className="text-sm font-medium text-blue-900">
                Viewing saved results
                {batchMeta.label && <> · <span className="font-semibold">{batchMeta.label}</span></>}
              </p>
              <p className="text-xs text-blue-800 mt-0.5">
                Originally scanned {fullDate(batchMeta.started_at)}. These results are not being refreshed —
                start a new scan below if you'd like updated data.
              </p>
            </div>
            <BatchStatusChip status={batchMeta.status} />
          </div>
        )}

        {/* Priority summary + cancel */}
        {rows.length > 0 && (
          <div className="flex flex-wrap items-center gap-3 mb-4">
            <div className="flex items-center gap-3 text-xs">
              <span className="text-charcoal-500">
                <BarChart3 className="w-3.5 h-3.5 inline mr-1" />
                {criticalCount} critical · {highCount} high · {skippedCount} healthy (deep skipped)
              </span>
            </div>
            {running && (
              <button onClick={cancelBatch}
                      data-testid="prospect-cancel-batch"
                      className="ml-auto px-3 py-1.5 rounded-full border border-red-200 bg-red-50 text-red-700 text-[10.5px] font-mono uppercase tracking-widest hover:border-red-300">
                <Ban className="w-3 h-3 inline mr-1" />
                Terminate batch
              </button>
            )}
          </div>
        )}

        {/* Iter-69 — Recommended Shortlist section. Main product now:
            sits ABOVE the 52-row table. Shortlist rows are qualified
            leads only (Confirmed / Strong-Signal + real personalization).
            Full results retained below. */}
        {batchId && (shortlist || shortlistLoading) && !running && (
          <ShortlistSection
            shortlist={shortlist}
            loading={shortlistLoading}
            onOpenDetail={(scanId) => setDetailFor(scanId)}
            onGenerateDraft={(scanId) => generateDraft(scanId)}
            onSaveLead={(scanId) => saveAsLead(scanId)}
            showPossibleStrip={showPossibleStrip}
            setShowPossibleStrip={setShowPossibleStrip}
          />
        )}

        {/* Results table */}
        {rows.length > 0 && (
          <div className="rounded-2xl bg-white border border-black/5 shadow-soft overflow-hidden mb-6" data-testid="prospect-results-table">
            <div className="px-4 py-3 border-b border-black/5 bg-charcoal-50/40 flex items-center gap-2 flex-wrap">
              <span className="text-[10px] font-mono uppercase tracking-widest text-charcoal-500">Full results ({rows.length})</span>
              <span className="text-[10px] text-charcoal-400">·</span>
              <span className="text-[10px] font-mono uppercase tracking-widest text-charcoal-500">Sort by</span>
              <button onClick={() => setSortKey("opportunity")}
                      data-testid="prospect-sort-opportunity"
                      className={`px-2.5 py-1 rounded-full text-[10.5px] font-mono uppercase tracking-widest border ${
                        sortKey === "opportunity"
                          ? "bg-charcoal-900 text-white border-charcoal-900"
                          : "bg-white text-charcoal-700 border-black/10 hover:border-black/20"}`}>
                Opportunity Score
              </button>
              <button onClick={() => setSortKey("priority")}
                      data-testid="prospect-sort-priority"
                      className={`px-2.5 py-1 rounded-full text-[10.5px] font-mono uppercase tracking-widest border ${
                        sortKey === "priority"
                          ? "bg-charcoal-900 text-white border-charcoal-900"
                          : "bg-white text-charcoal-700 border-black/10 hover:border-black/20"}`}>
                Priority band
              </button>
              <span className="text-[10px] font-mono uppercase tracking-widest text-charcoal-500 ml-4">Show</span>
              {[
                { k: "all",        label: "All" },
                { k: "fast_done",  label: "Scan complete" },
              ].map(f => (
                <button key={f.k} onClick={() => setStageFilter(f.k)}
                        data-testid={`prospect-filter-${f.k}`}
                        className={`px-2.5 py-1 rounded-full text-[10.5px] font-mono uppercase tracking-widest border ${
                          stageFilter === f.k
                            ? "bg-charcoal-900 text-white border-charcoal-900"
                            : "bg-white text-charcoal-700 border-black/10 hover:border-black/20"}`}>
                  {f.label}
                </button>
              ))}
            </div>
            {/* Iter-71 — Tier filter row. All / Critical / High /
                Moderate / Limited / Unverified / None. Counts reflect
                the exact tier the row will display (post-zero-coverage
                guard). Shortlist section above is unaffected. */}
            <div className="px-4 py-2.5 border-b border-black/5 bg-white flex items-center gap-2 flex-wrap"
                 data-testid="prospect-tier-filter-row">
              <span className="text-[10px] font-mono uppercase tracking-widest text-charcoal-500">Tier</span>
              {[
                { k: "all",        label: "All",        count: (tierCounts.critical + tierCounts.high + tierCounts.moderate + tierCounts.limited + tierCounts.unverified + tierCounts.none) },
                { k: "critical",   label: "Critical",   count: tierCounts.critical,   cls: "text-red-800" },
                { k: "high",       label: "High",       count: tierCounts.high,       cls: "text-amber-800" },
                { k: "moderate",   label: "Moderate",   count: tierCounts.moderate,   cls: "text-yellow-800" },
                { k: "limited",    label: "Limited",    count: tierCounts.limited,    cls: "text-charcoal-600" },
                { k: "unverified", label: "Unverified", count: tierCounts.unverified, cls: "text-charcoal-600" },
                { k: "none",       label: "None",       count: tierCounts.none,       cls: "text-emerald-800" },
              ].map(f => {
                const active = tierFilter === f.k;
                const baseCls = active
                  ? "bg-charcoal-900 text-white border-charcoal-900"
                  : `bg-white ${f.cls || "text-charcoal-700"} border-black/10 hover:border-black/20`;
                return (
                  <button key={f.k} onClick={() => setTierFilter(f.k)}
                          data-testid={`prospect-tier-filter-${f.k}`}
                          className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-[10.5px] font-mono uppercase tracking-widest border ${baseCls}`}>
                    {f.label}
                    <span className={`text-[10px] px-1 rounded ${active ? "bg-white/20" : "bg-charcoal-50"}`}>
                      {f.count}
                    </span>
                  </button>
                );
              })}
            </div>
            <table className="w-full text-sm">
              <thead className="bg-charcoal-50/60 text-[10px] font-mono uppercase tracking-widest text-charcoal-500">
                <tr>
                  <th className="text-left px-4 py-3">Organization</th>
                  <th className="text-left px-4 py-3">Finding</th>
                  <th className="text-left px-4 py-3">Opportunity</th>
                  <th className="text-left px-4 py-3 w-2/5">Evidence</th>
                  <th></th>
                </tr>
              </thead>
              <tbody>
                {filteredRows.map((r) => {
                  // Iter-66 — derive ONE coherent finding from the
                  // audit verdict (if present) or a defensive
                  // downgrade for legacy contradictions. This is the
                  // single source of truth the UI shows the owner —
                  // priority, opportunity, evidence, and actions all
                  // key off `f.tier`.
                  const f = deriveFinding(r);
                  // Iter-68 — AI-powered "Outreach email" is strictly
                  // limited to Critical/High. Moderate/None/Limited/
                  // Unverified never surface the button (backend also
                  // gates AI to Critical/High only in `outreach_drafter`).
                  const canDraft = ["fast_done_promising", "deep_running", "done"].includes(r.stage)
                                    && (f.tier === "critical" || f.tier === "high");
                  // Iter-71h — a row is "deep-audit interrupted / failed"
                  // when the fast phase succeeded (row carries a
                  // donation_audit / research summary from fast) but
                  // the deep phase produced an error OR the row was
                  // force-terminated by the pre-Iter-71g 15-min
                  // sweep. Such rows must remain VISIBLE + carry a
                  // Retry Deep Audit action.
                  const hasFastResult = !!(r?.donation_audit || r?.prospect_research);
                  const isDeepInterrupted =
                    (r.stage === "terminated" && hasFastResult) ||
                    (r.stage === "failed"     && hasFastResult && !!r.deep_error) ||
                    (r.stage === "fast_done_promising" && !!r.deep_error);
                  const canRequestDeep =
                    r.stage === "fast_done_healthy" ||
                    r.stage === "fast_done_promising" ||
                    isDeepInterrupted;
                  const canOpen =
                    ["fast_done_healthy", "fast_done_promising",
                     "deep_running", "done"].includes(r.stage) ||
                    isDeepInterrupted;
                  // Iter-68 — Detail panel opens for any row with a
                  // completed research payload, regardless of tier.
                  const hasResearch = !!(r?.prospect_research?.evidence_summary
                                          || r?.prospect_research?.score_breakdown);
                  return (
                    <>
                      <tr key={r.id} className="border-t border-black/5 hover:bg-charcoal-50/40"
                          data-testid={`prospect-row-${r.id}`}>
                        <td className="px-4 py-3">
                          <div className="font-medium text-charcoal-900">{r.organization_name}</div>
                          <div className="text-xs text-charcoal-500">{r.website}</div>
                          <div className="mt-1"><StageChip stage={r.stage} /></div>
                        </td>
                        <td className="px-4 py-3">
                          {canOpen ? <FindingChip finding={f} />
                            : <span className="text-xs text-charcoal-400">—</span>}
                        </td>
                        <td className="px-4 py-3">
                          {canOpen ? <OpportunityCell row={r} finding={f} />
                            : <span className="text-xs text-charcoal-400">—</span>}
                        </td>
                        <td className="px-4 py-3 text-xs text-charcoal-700">
                          {canOpen ? <EvidenceCell row={r} finding={f} />
                            : <span className="text-charcoal-400 italic">Waiting…</span>}
                        </td>
                        <td className="px-4 py-3 text-right whitespace-nowrap">
                          {canOpen && (
                            <div className="inline-flex gap-1 flex-wrap justify-end">
                              <button onClick={() => setExpanded(expanded === r.id ? null : r.id)}
                                      data-testid={`prospect-report-btn-${r.id}`}
                                      className="px-2 py-1 rounded-full border border-black/10 bg-white text-[10px] font-mono uppercase tracking-widest text-charcoal-700 hover:border-black/20">
                                <FileText className="w-3 h-3 inline mr-1" />
                                {expanded === r.id ? "Hide report" : "Report"}
                              </button>
                              {hasResearch && (
                                <button onClick={() => setDetailFor(r.id)}
                                        data-testid={`prospect-detail-btn-${r.id}`}
                                        className="px-2 py-1 rounded-full border border-blue-200 bg-blue-50 text-[10px] font-mono uppercase tracking-widest text-blue-700 hover:border-blue-300">
                                  <Info className="w-3 h-3 inline mr-1" />
                                  Details
                                </button>
                              )}
                              {canRequestDeep && (
                                <button onClick={() => requestDeep(r.id)}
                                        data-testid={`prospect-deep-btn-${r.id}`}
                                        className="px-2 py-1 rounded-full border border-indigo-200 bg-indigo-50 text-[10px] font-mono uppercase tracking-widest text-indigo-700 hover:border-indigo-300">
                                  <ArrowUpCircle className="w-3 h-3 inline mr-1" />
                                  {isDeepInterrupted ? "Retry Deep Audit" : "Run Deep Audit"}
                                </button>
                              )}
                              {canDraft && (
                                <button onClick={() => generateDraft(r.id)}
                                        data-testid={`prospect-draft-btn-${r.id}`}
                                        className="px-2 py-1 rounded-full border border-black/10 bg-white text-[10px] font-mono uppercase tracking-widest text-charcoal-700 hover:border-black/20">
                                  <Sparkles className="w-3 h-3 inline mr-1" />
                                  Outreach email
                                </button>
                              )}
                              {r.saved_as_lead_id ? (
                                <Link to={`/admin/leads/${r.saved_as_lead_id}`}
                                      className="px-2 py-1 rounded-full border border-emerald-200 bg-emerald-50 text-[10px] font-mono uppercase tracking-widest text-emerald-700 hover:border-emerald-300">
                                  <ExternalLink className="w-3 h-3 inline mr-1" />
                                  Lead ↗
                                </Link>
                              ) : (
                                <button onClick={() => saveAsLead(r.id)}
                                        data-testid={`prospect-save-lead-${r.id}`}
                                        className="px-2 py-1 rounded-full border border-emerald-200 bg-emerald-50 text-[10px] font-mono uppercase tracking-widest text-emerald-700 hover:border-emerald-300">
                                  <Save className="w-3 h-3 inline mr-1" />
                                  Save
                                </button>
                              )}
                            </div>
                          )}
                        </td>
                      </tr>
                      {expanded === r.id && (
                        <ExpandedReport key={`${r.id}-x`} scanId={r.id} />
                      )}
                    </>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}

        {/* Draft modal */}
        {draftFor && (
          <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4"
               onClick={() => setDraftFor(null)}
               data-testid="draft-modal">
            <div className="bg-white rounded-2xl max-w-2xl w-full p-6 shadow-2xl" onClick={(e) => e.stopPropagation()}>
              <div className="flex items-center justify-between mb-3">
                <div>
                  <p className="text-[10px] font-mono uppercase tracking-widest text-charcoal-400">Outreach draft</p>
                  <h3 className="text-lg font-medium text-charcoal-900">First draft — personalize and send yourself</h3>
                </div>
                <button onClick={() => setDraftFor(null)} className="text-charcoal-400 hover:text-charcoal-900 text-2xl">×</button>
              </div>
              {draftLoading ? (
                <div className="py-8 text-center">
                  <Loader2 className="w-6 h-6 animate-spin text-charcoal-400 mx-auto" />
                  <p className="mt-3 text-sm text-charcoal-500">Drafting…</p>
                </div>
              ) : (
                <>
                  <textarea readOnly value={draftText}
                            data-testid="draft-text"
                            rows={14}
                            className="w-full px-3 py-2 rounded-lg border border-black/10 text-sm font-mono bg-charcoal-50/40" />
                  <p className="mt-2 text-[11px] text-charcoal-500"
                     data-testid="draft-workflow-caption">
                    Copy → paste into your email client → hit Save to add to Leads.
                  </p>
                  <div className="mt-3 flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <p className="text-[10px] font-mono uppercase tracking-widest text-charcoal-500">
                        Source: {draftSource}
                      </p>
                      {draftAiUsed && (
                        <span
                          data-testid="draft-ai-badge"
                          className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded-full border border-indigo-200 bg-indigo-50 text-indigo-700 text-[9.5px] font-mono uppercase tracking-widest"
                        >
                          <Sparkles className="w-2.5 h-2.5" /> AI-personalized
                        </span>
                      )}
                    </div>
                    <button onClick={() => navigator.clipboard.writeText(draftText)}
                            data-testid="draft-copy"
                            className="inline-flex items-center gap-1 px-3 py-1.5 rounded-full bg-charcoal-900 text-white text-xs font-mono uppercase tracking-widest">
                      <Copy className="w-3 h-3" /> Copy draft
                    </button>
                  </div>
                </>
              )}
            </div>
          </div>
        )}

        {/* Empty state */}
        {rows.length === 0 && !running && (
          <div className="rounded-2xl bg-white border border-black/5 shadow-soft p-8 text-center" data-testid="prospect-empty">
            <ShieldAlert className="w-8 h-8 text-charcoal-300 mx-auto" />
            <p className="mt-3 text-sm text-charcoal-500">
              Paste nonprofit website URLs above (one per line) and click <span className="font-medium text-charcoal-900">Scan Prospects</span>.
            </p>
            <p className="mt-1 text-xs text-charcoal-400">Only confirmed findings generate outreach recommendations. Never contacts the target organizations.</p>
          </div>
        )}
      </div>

      {/* Iter-66 — Batch delete confirmation modal. Verbatim copy the
          owner specified: "Delete this batch and all N scan results?
          This cannot be undone." */}
      {deleteTarget && (
        <div
          role="dialog"
          aria-modal="true"
          aria-labelledby="delete-batch-title"
          data-testid="delete-batch-modal"
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 backdrop-blur-sm px-4"
          onClick={() => !deleteBusy && setDeleteTarget(null)}
        >
          <div
            onClick={(e) => e.stopPropagation()}
            className="w-full max-w-md rounded-2xl bg-white shadow-2xl border border-black/5 p-6"
          >
            <div className="flex items-start gap-3">
              <div className="mt-0.5 flex h-9 w-9 items-center justify-center rounded-full bg-red-50 text-red-700 border border-red-200">
                <Trash2 className="w-4 h-4" />
              </div>
              <div className="flex-1">
                <h3 id="delete-batch-title" className="text-base font-semibold text-charcoal-900">
                  Delete this batch and all {deleteTarget.total} scan result{deleteTarget.total === 1 ? "" : "s"}?
                </h3>
                <p className="mt-1.5 text-sm text-charcoal-600">
                  This cannot be undone.
                </p>
                {deleteTarget.label && (
                  <p className="mt-3 text-xs font-mono text-charcoal-500 truncate">
                    {deleteTarget.label}
                  </p>
                )}
                <p className="mt-3 text-xs text-charcoal-500">
                  Leads you already saved to the CRM will remain untouched.
                </p>
              </div>
            </div>
            <div className="mt-6 flex justify-end gap-2">
              <button
                type="button"
                onClick={() => setDeleteTarget(null)}
                disabled={deleteBusy}
                data-testid="delete-batch-cancel"
                className="px-3.5 py-2 rounded-full border border-black/10 bg-white text-sm text-charcoal-700 hover:border-black/20 disabled:opacity-50"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={confirmDeleteBatch}
                disabled={deleteBusy}
                data-testid="delete-batch-confirm"
                className="inline-flex items-center gap-1.5 px-3.5 py-2 rounded-full bg-red-600 text-white text-sm font-medium hover:bg-red-700 disabled:opacity-60"
              >
                {deleteBusy ? (
                  <><Loader2 className="w-3.5 h-3.5 animate-spin" /> Deleting…</>
                ) : (
                  <>Delete batch</>
                )}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Iter-68 — Row detail panel. Shows the full deterministic
          research evidence (pages inspected, destinations probed,
          org summary, programs, leadership, location, personalization
          quality, positive observations). AI is NEVER used to
          generate this — it's a pure projection of the structured
          scan doc. Isolated from batch completion: if this panel
          crashes for any reason (unlikely, but defensive), it never
          affects the batch results. */}
      {detailFor && (
        <DetailPanel
          scanId={detailFor}
          rows={rows}
          onClose={() => setDetailFor(null)}
        />
      )}
    </div>
  );
}

function OpportunityCell({ row, finding }) {
  // Iter-68 — the chip shows both the rule-based Tier label AND the
  // additive numeric score (0-100). Score ranks WITHIN the tier and
  // never promotes across tiers. Clicking the chip expands the
  // deterministic score-breakdown pulled from the backend research
  // module (`prospect_research.score_breakdown.components`) so the
  // owner can see EXACTLY which signals contributed how many points.
  const [open, setOpen] = useState(false);
  const tier = finding?.tier || "none";
  let score = row.opportunity_score || 0;
  // Iter-71b — spam / hijack forces score to 0 in the table.
  // Iter-71d — a failed deep audit is treated the same way (no
  // outreach claim). `deep_complete` keeps whatever score the row
  // carries so the owner can still rank the deep-audited rows.
  if (tier === "none" || tier === "unverified" || tier === "limited"
       || tier === "spam" || tier === "deep_failed")
    score = 0;

  const tierLabel = {
    critical:      "Critical",
    high:          "High",
    moderate:      "Moderate",
    limited:       "Limited",
    unverified:    "Unverified",
    spam:          "Unverified",
    deep_complete: "Deep",
    deep_failed:   "Deep",
    none:          "None",
  }[tier] || "None";

  const cls = tier === "critical" ? "bg-red-50 text-red-800 border-red-200 hover:border-red-300"
             : tier === "high"    ? "bg-amber-50 text-amber-800 border-amber-200 hover:border-amber-300"
             : tier === "moderate" ? "bg-yellow-50 text-yellow-800 border-yellow-200 hover:border-yellow-300"
             : tier === "deep_complete" ? "bg-emerald-50 text-emerald-800 border-emerald-200 hover:border-emerald-300"
             : tier === "deep_failed"   ? "bg-red-50 text-red-800 border-red-200"
             : tier === "limited" || tier === "unverified" || tier === "spam"
                                  ? "bg-charcoal-50 text-charcoal-500 border-black/10"
             :                       "bg-emerald-50 text-emerald-700 border-emerald-200 hover:border-emerald-300";

  const research = row?.prospect_research || {};
  const sb = research?.score_breakdown || {};
  const components = Array.isArray(sb?.components) ? sb.components : [];
  const hasBreakdown = components.length > 0;

  const humanSignal = (s) => ({
    broken_donation_path:          "Confirmed broken donation path",
    homepage_or_ssl_failure:       "Homepage or SSL failure",
    no_donation_path_completed:    "No donation path found after full audit",
    broken_donor_facing_link:      "Broken donor-facing link",
    multiple_inconsistent_giving:  "Multiple inconsistent giving hosts",
    donation_buried:               "Donate link is buried",
    missing_contact:               "Missing contact page",
    weak_cta:                      "Weak / missing above-the-fold CTA",
    stale_content:                 "Stale content (old copyright / campaign)",
  }[s] || s);

  return (
    <div className="min-w-[140px]">
      <button
        type="button"
        onClick={() => hasBreakdown && setOpen((v) => !v)}
        disabled={!hasBreakdown}
        data-testid={`opportunity-${tier}`}
        className={`inline-flex items-center gap-1.5 px-2 py-0.5 rounded-full border text-[10.5px] font-mono uppercase tracking-widest ${cls} ${hasBreakdown ? "cursor-pointer" : "cursor-default"}`}
      >
        <span className="font-semibold">{tierLabel}</span>
        <span className="opacity-60">·</span>
        <span className="text-charcoal-900 font-semibold">{score}</span>
        <span>/ 100</span>
        {hasBreakdown && (open
          ? <ChevronUp className="w-3 h-3 opacity-70" />
          : <ChevronDown className="w-3 h-3 opacity-70" />)}
      </button>
      {open && hasBreakdown && (
        <div
          data-testid={`opportunity-breakdown-${row.id}`}
          className="mt-2 rounded-lg border border-black/10 bg-white p-2.5 shadow-soft space-y-1"
        >
          <p className="text-[10px] font-mono uppercase tracking-widest text-charcoal-400 mb-1">
            Score breakdown · {sb.total ?? score}/100
          </p>
          {components.map((c, i) => (
            <div
              key={i}
              data-testid={`opportunity-breakdown-item-${i}`}
              className="flex items-baseline gap-2 text-[11px]"
            >
              <span className="inline-block w-9 text-right font-mono font-semibold text-charcoal-900">
                +{c.points}
              </span>
              <div className="flex-1">
                <div className="text-charcoal-900">{humanSignal(c.signal)}</div>
                {c.evidence && (
                  <div className="text-charcoal-500 text-[10.5px] font-mono truncate" title={c.evidence}>
                    {c.evidence}
                  </div>
                )}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

// Iter-67 — Evidence column. Surfaces the exact coverage numbers
// (pages inspected, destinations discovered/probed) so the owner
// can see WHY a row got its label. Broken paths render inline
// evidence (anchor + source + destination + failure). Limited /
// blocked / healthy each get their own explanatory copy.
function EvidenceCell({ row, finding }) {
  const audit = row?.donation_audit || {};
  const broken = (audit.broken_paths || [])[0];
  const blocked = (audit.blocked_paths || [])[0];
  const inspected = audit.pages_inspected ?? audit.pages_crawled ?? 0;
  const discovered = audit.pages_discovered ?? 0;
  const dests_disc = audit.destinations_discovered ?? 0;
  const dests_probed = audit.destinations_probed ?? 0;
  const status = audit.audit_status;
  const stopReason = (audit.stop_reason || "").replace(/_/g, " ");

  const coverageChip = (
    <div className="mt-1 text-[10.5px] font-mono text-charcoal-500">
      {inspected} inspected · {discovered} discovered ·
      {" "}{dests_probed}/{dests_disc || 0} dest probed
      {status === "completed_partial" && stopReason && (
        <span className="ml-1 text-amber-700">(stopped: {stopReason})</span>
      )}
    </div>
  );

  if (finding.tier === "critical" && broken) {
    const anchor = (broken.anchor_text || "Donate").trim();
    const source = broken.source_page || row.website;
    const dest = broken.destination || "";
    const fk = broken.failure_kind || "connection";
    const statusMap = {
      http_404:      `returned 404`,
      http_5xx:      `returned ${broken.status || "a server error"}`,
      connection:    "could not connect",
      ssl:           "failed SSL",
      timeout:       "timed out",
      redirect_loop: "hit a redirect loop",
    };
    const failure = statusMap[fk] || `returned ${broken.status || "an error"}`;
    return (
      <div className="text-xs text-charcoal-700 leading-tight">
        <span className="font-medium">"{anchor}"</span> on <span className="font-mono text-charcoal-500">{shortUrl(source)}</span> → <span className="font-mono text-charcoal-500">{shortUrl(dest)}</span> <span className="text-red-700">{failure}</span>
        {audit.broken_paths.length > 1 && (
          <span className="ml-1 text-charcoal-400">
            (+{audit.broken_paths.length - 1} more)
          </span>
        )}
        {coverageChip}
      </div>
    );
  }
  if (finding.tier === "high") {
    return (
      <div className="text-xs text-charcoal-700 leading-tight">
        Completed audit found no donation destination across the
        bounded crawl.
        {coverageChip}
      </div>
    );
  }
  if (finding.tier === "limited") {
    return (
      <div className="text-xs text-charcoal-500 leading-tight italic">
        Coverage below the 8-page floor — audit could not
        adequately evaluate donation paths.
        No outreach claim.
        {coverageChip}
      </div>
    );
  }
  if (finding.tier === "spam") {
    // Iter-71b — spam / hijack downgrade. Copy is intentionally
    // curt: the raw audit is still viewable in the Detail drawer,
    // but the table itself should not encourage outreach.
    const reason = row?.spam_reason
      ? row.spam_reason.replace(/_/g, " ")
      : "spam signature detected";
    return (
      <div
        data-testid="evidence-spam"
        className="text-xs text-charcoal-500 leading-tight italic"
      >
        Possible spam or compromised domain ({reason}).
        Not usable for outreach. See Detail for raw audit.
        {coverageChip}
      </div>
    );
  }
  if (finding.tier === "deep_complete") {
    // Iter-71d — post-Deep-Audit row summary. Renders the concise
    // "what the deep audit found" bullet list the owner asked for.
    // Coverage chip is intentionally suppressed here — pages_scanned
    // already appears in the first bullet so the fast-scan
    // "0 inspected / 0 discovered" line would be misleading.
    const das = row?.deep_audit_summary || {};
    const bullets = [];
    if (das.pages_scanned)
      bullets.push(`${das.pages_scanned} page${das.pages_scanned === 1 ? "" : "s"} scanned`);
    (das.highlights || []).forEach(h => bullets.push(h));
    return (
      <div
        data-testid="evidence-deep-complete"
        className="text-xs text-charcoal-700 leading-tight"
      >
        {bullets.length ? (
          <ul className="list-disc pl-4 space-y-0.5">
            {bullets.map((b, i) => <li key={i}>{b}</li>)}
          </ul>
        ) : (
          <span className="italic text-charcoal-500">
            Deep audit complete — see Detail for the full report.
          </span>
        )}
      </div>
    );
  }
  if (finding.tier === "deep_failed") {
    // Iter-71d — deep audit ran but errored. Show the reason instead
    // of pretending the row is still Limited/Unverified.
    const das = row?.deep_audit_summary || {};
    const reason = das.failure_reason || "unknown error";
    return (
      <div
        data-testid="evidence-deep-failed"
        className="text-xs text-red-700 leading-tight"
      >
        Deep audit failed — {reason}. See Detail for the full trace.
      </div>
    );
  }
  if (finding.tier === "unverified") {
    if (blocked) {
      const reason = (blocked.block_reason || "").replace(/_/g, " ");
      return (
        <div className="text-xs text-charcoal-500 leading-tight italic">
          Donation destination detected but automated verification was
          blocked{reason ? ` (${reason})` : ""}.
          No outreach claim.
          {coverageChip}
        </div>
      );
    }
    return (
      <div className="text-xs text-charcoal-500 italic">
        Scanner could not obtain a coherent answer for this row.
        {coverageChip}
      </div>
    );
  }
  if (finding.tier === "moderate") {
    // Iter-71 — Moderate rows expose the exact score-driving signals
    // inline so the owner can see WHY the row was scored Moderate
    // without opening the detail drawer.  Pulls from the same
    // `prospect_research.score_breakdown.components` payload used by
    // the Opportunity chip breakdown — no new scoring logic.
    const HUMAN = {
      donation_buried:                "Donate link buried",
      missing_contact:                "Contact path missing",
      weak_cta:                       "Primary CTA weak",
      stale_content:                  "Content looks stale",
      multiple_inconsistent_giving:   "Giving hosts inconsistent",
      broken_donor_facing_link:       "Donor-facing link broken",
    };
    const components = (row?.prospect_research?.score_breakdown?.components) || [];
    const labels = components
      .map(c => HUMAN[c.signal])
      .filter(Boolean);
    if (labels.length) {
      return (
        <div
          data-testid="evidence-moderate"
          className="text-xs text-charcoal-700 leading-tight"
        >
          {labels.join(" · ")}
          {coverageChip}
        </div>
      );
    }
    return (
      <div
        data-testid="evidence-moderate"
        className="text-xs text-charcoal-500 italic leading-tight"
      >
        Moderate friction signals detected — see detail drawer.
        {coverageChip}
      </div>
    );
  }
  return (
    <div className="text-xs text-charcoal-700 italic">
      No broken donation paths found in completed audit.
      {coverageChip}
    </div>
  );
}

function shortUrl(u) {
  if (!u) return "";
  try {
    const url = new URL(u);
    const path = url.pathname === "/" ? "" : url.pathname;
    return url.hostname.replace(/^www\./, "") + path;
  } catch (_) { return u; }
}

function ExpandedReport({ scanId }) {
  const [rep, setRep] = useState(null);
  useEffect(() => {
    (async () => {
      try {
        const r = await api.get(`/admin/prospect-scans/${scanId}`);
        setRep(r.data);
      } catch (_) { setRep({ error: true }); }
    })();
  }, [scanId]);
  if (!rep) return <tr><td colSpan={7} className="px-8 py-3 text-xs text-charcoal-400">Loading…</td></tr>;
  if (rep.error) return <tr><td colSpan={7} className="px-8 py-3 text-xs text-red-600">Could not load report.</td></tr>;
  const checks = rep?.report?.checks || [];
  return (
    <tr className="bg-charcoal-50/30">
      <td colSpan={7} className="px-8 py-4">
        {rep.promotion_reason && (
          <p className="text-[11px] font-mono uppercase tracking-widest text-charcoal-500 mb-3">
            Deep audit decision — {rep.promoted_to_deep ? "ran" : "skipped"}: <span className="normal-case tracking-normal font-sans text-charcoal-700">{rep.promotion_reason}</span>
          </p>
        )}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          {checks.map((c, i) => {
            const st = c.state ? c.state.replace(/_/g, " ") : c.status;
            return (
              <div key={i} className="text-xs">
                <div className="flex items-baseline gap-2">
                  <span className={`inline-block w-2 h-2 rounded-full ${
                    c.status === "green" ? "bg-emerald-500" :
                    c.status === "amber" ? "bg-amber-500" :
                    c.status === "red"   ? "bg-red-500" :
                                            "bg-charcoal-300"}`} />
                  <span className="font-medium text-charcoal-900">{c.label}</span>
                  <span className="text-[10px] font-mono uppercase tracking-widest text-charcoal-500">{st}</span>
                </div>
                <p className="text-charcoal-600 pl-4 mt-0.5">{c.detail}</p>
              </div>
            );
          })}
        </div>
      </td>
    </tr>
  );
}


// ---------------------------------------------------------------------- //


// ---------------------------------------------------------------------- //
// Iter-69 — Recommended Shortlist section.
// Sits at the top of a completed batch, above the 52-row table. Shows:
//   • Best prospects to contact (qualified — Confirmed / Strong-Signal
//     + real personalization + rank_score ≥ 40, cap 10)
//   • Needs manual review (Unverified / blocked probes / spam-hijack)
//   • Findings without enough context to personalize (Possible only,
//     collapsed by default)
// Outreach Email button surfaces ONLY on shortlisted rows.
// ---------------------------------------------------------------------- //
function ShortlistSection({
  shortlist, loading, onOpenDetail, onGenerateDraft, onSaveLead,
  showPossibleStrip, setShowPossibleStrip,
}) {
  // Iter-70 — three phases.
  //   HTTP audit complete → Browser verification running X/12 → Final shortlist ready
  // We NEVER publish a stale interim shortlist while browser phase is
  // in {queued, running}.  Backend returns `phase: "browser_verification"`
  // in that case with the progress payload.
  if (loading && !shortlist) {
    return (
      <div className="rounded-2xl bg-white border border-black/5 shadow-soft p-6 mb-6"
           data-testid="shortlist-loading">
        <div className="flex items-center gap-2 text-charcoal-500">
          <Loader2 className="w-4 h-4 animate-spin" />
          <span className="text-sm">Building recommended shortlist…</span>
        </div>
      </div>
    );
  }
  if (!shortlist) return null;

  if (shortlist.phase === "browser_verification") {
    const p = shortlist.browser_job || {};
    const prog = p.progress || {};
    const total = prog.total || 0;
    const done  = (prog.completed || 0) + (prog.timed_out || 0) + (prog.failed || 0);
    return (
      <section className="mb-6" data-testid="shortlist-browser-phase">
        <div className="rounded-2xl bg-charcoal-900 text-white p-6 shadow-soft">
          <p className="text-[10px] font-mono uppercase tracking-widest text-white/50">
            Phase 2 of 3
          </p>
          <h2 className="text-xl font-semibold tracking-tight mt-0.5">
            Browser verification running · {done}/{total}
          </h2>
          <div className="mt-3 text-[12px] text-white/70 flex flex-wrap gap-x-4 gap-y-1">
            <span className="text-emerald-400">✓ HTTP audit complete</span>
            <span className="text-white">
              <Loader2 className="inline w-3 h-3 animate-spin mr-1" />
              Verifying {prog.in_flight || 0} in flight
            </span>
            <span className="text-white/50">· Final shortlist ready when done</span>
          </div>
          <div className="mt-3 h-1 rounded bg-white/10 overflow-hidden">
            <div
              className="h-full bg-emerald-400"
              style={{ width: total > 0 ? `${Math.round((done/total)*100)}%` : "0%" }}
            />
          </div>
          <p className="mt-3 text-[10px] font-mono text-white/40">
            Browser verification is bounded to 45 s per row and 15 min per batch.
            Shortlist will publish automatically when this phase terminates.
          </p>
        </div>
      </section>
    );
  }
  const { shortlist: top, needs_manual_review = [], possible_only = [], totals = {}, browser_job } = shortlist;
  const bjFailed = browser_job && browser_job.state === "failed";
  const bjTimedOut = browser_job && browser_job.state === "timed_out";
  const bjDisabled = !browser_job || browser_job.state === "disabled";

  return (
    <section className="mb-6 space-y-4" data-testid="shortlist-section">
      <div className="rounded-2xl bg-charcoal-900 text-white p-6 shadow-soft">
        <div className="flex items-baseline justify-between flex-wrap gap-2">
          <div>
            <p className="text-[10px] font-mono uppercase tracking-widest text-white/50">
              Final shortlist ready
            </p>
            <h2 className="text-xl font-semibold tracking-tight mt-0.5">
              Best prospects to contact
            </h2>
          </div>
          <p className="text-[11px] font-mono uppercase tracking-widest text-white/60">
            {totals.shortlisted ?? 0} of {totals.total ?? 0} shortlisted ·
            {" "}{totals.manual_review ?? 0} need manual review
            {totals.spam_downgraded ? ` · ${totals.spam_downgraded} spam-flagged` : ""}
          </p>
        </div>
        {/* Iter-70 — browser phase telemetry stripe */}
        <div className="mt-3 flex flex-wrap gap-x-4 gap-y-1 text-[10.5px] font-mono uppercase tracking-widest text-white/60"
             data-testid="browser-phase-telemetry">
          {bjDisabled && (
            <span data-testid="browser-phase-badge-disabled">
              Browser verification: this batch was scanned with the flag off
            </span>
          )}
          {!bjDisabled && browser_job.state === "completed" && (
            <span className="text-emerald-400">Browser verification: completed
              {browser_job.progress && ` · ${browser_job.progress.completed || 0}/${browser_job.progress.total || 0} verified`}
              {browser_job.wall_clock_s != null && ` · ${Math.round(browser_job.wall_clock_s)}s`}
            </span>
          )}
          {bjTimedOut && (
            <span className="text-amber-400">
              Browser verification: timed out (partial results kept)
            </span>
          )}
          {bjFailed && (
            <span className="text-red-400">
              Browser verification: failed — Iter-69 result preserved
              {browser_job.failure_reason && ` (${browser_job.failure_reason})`}
            </span>
          )}
        </div>
      </div>

      {top.length === 0 ? (
        <div
          data-testid="shortlist-empty"
          className="rounded-2xl bg-white border border-dashed border-black/10 p-6 text-sm text-charcoal-600"
        >
          <p className="font-medium text-charcoal-900">
            Zero recommended leads from this batch.
          </p>
          <p className="mt-1 text-charcoal-500">
            The scanner didn't find any organization with both (a) a verified
            outreach concern at Confirmed or Strong-Signal confidence and (b)
            enough organizational context to write a specific first email.
            {" "}
            {totals.manual_review > 0 && (
              <>See <span className="font-mono text-charcoal-700">Needs manual review</span> below
                — {totals.manual_review} rows might be worth a 30-second human check.
              </>
            )}
          </p>
        </div>
      ) : (
        <div className="space-y-3">
          {top.map((lead, i) => (
            <LeadCard
              key={lead.id}
              rank={i + 1}
              lead={lead}
              onOpenDetail={onOpenDetail}
              onGenerateDraft={onGenerateDraft}
              onSaveLead={onSaveLead}
            />
          ))}
        </div>
      )}

      {needs_manual_review.length > 0 && (
        <div className="rounded-2xl bg-white border border-black/5 shadow-soft p-5"
             data-testid="shortlist-manual-review">
          <div className="flex items-baseline justify-between gap-3 mb-3">
            <div>
              <p className="text-[10px] font-mono uppercase tracking-widest text-charcoal-500">
                Needs manual review
              </p>
              <h3 className="text-base font-semibold text-charcoal-900">
                {needs_manual_review.length} row{needs_manual_review.length === 1 ? "" : "s"} · scanner couldn't verify automatically
              </h3>
            </div>
          </div>
          <div className="space-y-2">
            {needs_manual_review.slice(0, 12).map((lead) => (
              <ManualReviewRow key={lead.id} lead={lead} onOpenDetail={onOpenDetail} />
            ))}
          </div>
        </div>
      )}

      {possible_only.length > 0 && (
        <div className="rounded-2xl bg-white border border-black/5 shadow-soft"
             data-testid="shortlist-possible-strip">
          <button
            type="button"
            onClick={() => setShowPossibleStrip((v) => !v)}
            data-testid="shortlist-possible-toggle"
            className="w-full flex items-center justify-between px-5 py-4 text-left"
          >
            <div>
              <p className="text-[10px] font-mono uppercase tracking-widest text-charcoal-500">
                Findings without enough context to personalize
              </p>
              <h3 className="text-sm text-charcoal-700 mt-0.5">
                {possible_only.length} row{possible_only.length === 1 ? "" : "s"} — Possible Opportunity only, not shortlisted
              </h3>
            </div>
            {showPossibleStrip ? <ChevronUp className="w-4 h-4 text-charcoal-500" />
                                : <ChevronDown className="w-4 h-4 text-charcoal-500" />}
          </button>
          {showPossibleStrip && (
            <div className="border-t border-black/5 px-5 py-3 space-y-2">
              {possible_only.slice(0, 30).map((lead) => (
                <ManualReviewRow key={lead.id} lead={lead}
                                 onOpenDetail={onOpenDetail} tone="possible" />
              ))}
            </div>
          )}
        </div>
      )}
    </section>
  );
}


function LeadCard({ rank, lead, onOpenDetail, onGenerateDraft, onSaveLead }) {
  const top = lead.top_reason || {};
  const ev = lead.evidence || {};
  const audit = lead.audit_summary || {};
  const confidenceCls = top.confidence === "Confirmed"
    ? "bg-red-50 text-red-800 border-red-200"
    : top.confidence === "Strong Signal"
      ? "bg-amber-50 text-amber-800 border-amber-200"
      : "bg-charcoal-50 text-charcoal-600 border-black/10";
  return (
    <div
      data-testid={`lead-card-${lead.id}`}
      className="rounded-2xl bg-white border border-black/5 shadow-soft p-5"
    >
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div className="flex-1 min-w-0">
          <div className="flex items-baseline gap-2 flex-wrap">
            <span className="text-[10px] font-mono uppercase tracking-widest text-charcoal-400">
              #{rank}
            </span>
            <h4 className="text-lg font-medium text-charcoal-900 truncate">
              {lead.organization_name || ev.name || "Unnamed prospect"}
            </h4>
            {ev.location && (
              <span className="text-[11px] font-mono text-charcoal-500">
                · {ev.location}
              </span>
            )}
          </div>
          <a
            href={lead.website}
            target="_blank"
            rel="noopener noreferrer"
            className="text-xs font-mono text-charcoal-500 hover:text-charcoal-900"
          >
            {lead.website}
          </a>
        </div>
        <div className="flex items-center gap-2">
          <span
            data-testid={`lead-confidence-${lead.id}`}
            className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full border text-[10.5px] font-mono uppercase tracking-widest ${confidenceCls}`}
          >
            {top.confidence || "Unverified"}
          </span>
          <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full border border-black/10 bg-charcoal-50 text-charcoal-700 text-[10.5px] font-mono uppercase tracking-widest">
            rank {lead.rank_score}/100
          </span>
        </div>
      </div>

      {/* Why contact them */}
      <div className="mt-4">
        <p className="text-[10px] font-mono uppercase tracking-widest text-charcoal-500 mb-1">
          Why contact them
        </p>
        <p className="text-charcoal-900 text-[15px] leading-snug">
          {top.headline || "—"}
        </p>
      </div>

      {/* Evidence */}
      {top.evidence && (
        <div className="mt-3">
          <p className="text-[10px] font-mono uppercase tracking-widest text-charcoal-500 mb-1">
            Evidence
          </p>
          <p className="text-[13px] text-charcoal-700 font-mono">
            {top.evidence}
          </p>
        </div>
      )}

      {/* Organization context */}
      {(ev.mission || (ev.programs || []).length > 0) && (
        <div className="mt-3 border-t border-black/5 pt-3">
          <p className="text-[10px] font-mono uppercase tracking-widest text-charcoal-500 mb-1">
            Organization context
          </p>
          {ev.mission && (
            <p className="text-[13px] text-charcoal-700 leading-snug">
              {ev.mission.length > 240 ? ev.mission.slice(0, 240) + "…" : ev.mission}
            </p>
          )}
          <div className="mt-1.5 flex flex-wrap gap-x-4 gap-y-1 text-[11.5px] text-charcoal-500">
            {(ev.programs || []).length > 0 && (
              <span><span className="text-charcoal-400">Programs:</span> {ev.programs.slice(0, 3).join(" · ")}</span>
            )}
            {(ev.leadership || []).length > 0 && (
              <span><span className="text-charcoal-400">Leadership:</span> {ev.leadership.slice(0, 2).join(" · ")}</span>
            )}
            {(ev.campaign_names || []).length > 0 && (
              <span><span className="text-charcoal-400">Campaigns:</span> {ev.campaign_names.slice(0, 2).join(" · ")}</span>
            )}
            {(ev.platforms_detected || []).length > 0 && (
              <span><span className="text-charcoal-400">Platforms:</span> <span className="font-mono">{ev.platforms_detected.join(", ")}</span></span>
            )}
          </div>
        </div>
      )}

      {/* Actions */}
      <div className="mt-4 flex flex-wrap items-center gap-2">
        <button
          onClick={() => onOpenDetail(lead.id)}
          data-testid={`lead-view-detail-${lead.id}`}
          className="inline-flex items-center gap-1 px-3 py-1.5 rounded-full border border-black/10 bg-white text-[11px] font-mono uppercase tracking-widest text-charcoal-700 hover:border-black/20"
        >
          <Info className="w-3 h-3" />
          View full research
        </button>
        <button
          onClick={() => onGenerateDraft(lead.id)}
          data-testid={`lead-generate-outreach-${lead.id}`}
          className="inline-flex items-center gap-1 px-3 py-1.5 rounded-full bg-charcoal-900 text-white text-[11px] font-mono uppercase tracking-widest hover:bg-charcoal-800"
        >
          <Sparkles className="w-3 h-3" />
          Generate outreach email
        </button>
        <button
          onClick={() => onSaveLead(lead.id)}
          data-testid={`lead-save-${lead.id}`}
          className="inline-flex items-center gap-1 px-3 py-1.5 rounded-full border border-emerald-200 bg-emerald-50 text-[11px] font-mono uppercase tracking-widest text-emerald-700 hover:border-emerald-300"
        >
          <Save className="w-3 h-3" />
          Save lead
        </button>
      </div>
    </div>
  );
}


function ManualReviewRow({ lead, onOpenDetail, tone = "manual" }) {
  const top = lead.top_reason || {};
  const ev = lead.evidence || {};
  const dotCls = tone === "possible"
    ? "bg-yellow-500" : "bg-charcoal-400";
  return (
    <div
      data-testid={`manual-review-row-${lead.id}`}
      className="flex items-start gap-3 text-sm"
    >
      <span className={`inline-block w-2 h-2 rounded-full mt-1.5 ${dotCls}`} />
      <div className="flex-1 min-w-0">
        <div className="flex items-baseline gap-2 flex-wrap">
          <span className="font-medium text-charcoal-900">
            {lead.organization_name || ev.name || lead.website}
          </span>
          {ev.location && (
            <span className="text-[11px] font-mono text-charcoal-500">· {ev.location}</span>
          )}
          <span className="text-[10px] font-mono uppercase tracking-widest text-charcoal-400">
            {top.confidence || "Unverified"}
          </span>
        </div>
        <p className="text-[13px] text-charcoal-700 leading-snug">
          {top.headline || "No specific finding — inspect the full report."}
        </p>
      </div>
      <button
        onClick={() => onOpenDetail(lead.id)}
        data-testid={`manual-review-open-${lead.id}`}
        className="text-[10.5px] font-mono uppercase tracking-widest text-blue-700 hover:text-blue-900"
      >
        View
      </button>
    </div>
  );
}


// ---------------------------------------------------------------------- //
// Iter-68 — Row Detail Panel.
// Renders the full deterministic research evidence: pages inspected,
// stop reason, destinations probed, org summary, programs, leadership,
// location, personalization-quality chip, and positive factual
// observations. NO AI is invoked here — this is a pure projection of
// the already-collected `prospect_research.evidence` + `donation_audit`.
// If any field is missing the panel simply omits it; the panel is
// isolated from batch completion and never affects scanner results.
// ---------------------------------------------------------------------- //
function DetailPanel({ scanId, rows, onClose }) {
  const row = rows.find((r) => r.id === scanId) || null;
  const audit = row?.donation_audit || {};
  const research = row?.prospect_research || {};
  const evSum = research?.evidence_summary || {};
  const sb = research?.score_breakdown || {};
  const components = Array.isArray(sb?.components) ? sb.components : [];
  const pqLabel = ({
    strong: "Strong",
    fair:   "Fair",
    weak:   "Weak",
    none:   "None",
  }[research?.personalization_quality || "none"] || "None");
  const pqCls = ({
    strong: "bg-emerald-50 text-emerald-800 border-emerald-200",
    fair:   "bg-yellow-50 text-yellow-800 border-yellow-200",
    weak:   "bg-charcoal-50 text-charcoal-600 border-black/10",
    none:   "bg-charcoal-50 text-charcoal-500 border-black/10",
  }[research?.personalization_quality || "none"]);

  const stopReason = (audit.stop_reason || "").replace(/_/g, " ");
  const healthy = (audit.healthy_paths || []).slice(0, 8);
  const broken  = (audit.broken_paths  || []).slice(0, 8);
  const blocked = (audit.blocked_paths || []).slice(0, 4);

  return (
    <div
      className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4"
      onClick={onClose}
      data-testid="detail-panel"
    >
      <div
        className="bg-white rounded-2xl max-w-3xl w-full max-h-[85vh] overflow-y-auto shadow-2xl"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="sticky top-0 bg-white border-b border-black/5 px-6 py-4 flex items-center justify-between">
          <div>
            <p className="text-[10px] font-mono uppercase tracking-widest text-charcoal-400">
              Prospect research evidence
            </p>
            <h3
              data-testid="detail-panel-title"
              className="text-lg font-medium text-charcoal-900"
            >
              {row?.organization_name || evSum?.name || "Prospect detail"}
            </h3>
            {row?.website && (
              <a
                href={row.website}
                target="_blank"
                rel="noopener noreferrer"
                className="text-xs font-mono text-charcoal-500 hover:text-charcoal-900 inline-flex items-center gap-1"
              >
                {row.website}
                <ExternalLink className="w-3 h-3" />
              </a>
            )}
          </div>
          <button
            onClick={onClose}
            data-testid="detail-panel-close"
            className="text-charcoal-400 hover:text-charcoal-900"
            aria-label="Close detail panel"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        <div className="px-6 py-5 space-y-6">
          <section data-testid="detail-panel-coverage">
            <p className="text-[10px] font-mono uppercase tracking-widest text-charcoal-500 mb-2">
              Audit coverage
            </p>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3 text-sm">
              <Stat label="Pages inspected" value={audit.pages_inspected ?? audit.pages_crawled ?? 0} />
              <Stat label="Pages discovered" value={audit.pages_discovered ?? 0} />
              <Stat label="Destinations discovered" value={audit.destinations_discovered ?? 0} />
              <Stat label="Destinations probed" value={audit.destinations_probed ?? 0} />
            </div>
            {audit.audit_status === "completed_partial" && stopReason && (
              <p className="mt-2 text-xs text-amber-700">
                Stopped early: <span className="font-mono">{stopReason}</span>
              </p>
            )}
            {audit.verdict && (
              <p className="mt-2 text-xs text-charcoal-500">
                Verdict: <span className="font-mono uppercase tracking-widest">{audit.verdict.replace(/_/g, " ")}</span>
              </p>
            )}
          </section>

          {(healthy.length > 0 || broken.length > 0 || blocked.length > 0) && (
            <section data-testid="detail-panel-destinations">
              <p className="text-[10px] font-mono uppercase tracking-widest text-charcoal-500 mb-2">
                Destinations probed
              </p>
              <div className="space-y-1.5 text-xs">
                {broken.map((p, i) => (
                  <div key={`b-${i}`} className="flex items-baseline gap-2">
                    <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded-full border border-red-200 bg-red-50 text-red-700 text-[9.5px] font-mono uppercase tracking-widest">
                      <XCircle className="w-2.5 h-2.5" /> broken
                    </span>
                    <span className="font-mono text-charcoal-700 truncate">{p.destination}</span>
                    <span className="text-charcoal-400">{p.failure_kind || p.status}</span>
                  </div>
                ))}
                {healthy.map((p, i) => (
                  <div key={`h-${i}`} className="flex items-baseline gap-2">
                    <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded-full border border-emerald-200 bg-emerald-50 text-emerald-700 text-[9.5px] font-mono uppercase tracking-widest">
                      <CheckCircle2 className="w-2.5 h-2.5" /> healthy
                    </span>
                    <span className="font-mono text-charcoal-700 truncate">{p.destination}</span>
                    {p.location && (
                      <span className="text-charcoal-400 uppercase text-[10px] font-mono tracking-widest">{p.location}</span>
                    )}
                  </div>
                ))}
                {blocked.map((p, i) => (
                  <div key={`x-${i}`} className="flex items-baseline gap-2">
                    <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded-full border border-black/10 bg-charcoal-50 text-charcoal-600 text-[9.5px] font-mono uppercase tracking-widest">
                      <ShieldAlert className="w-2.5 h-2.5" /> blocked
                    </span>
                    <span className="font-mono text-charcoal-700 truncate">{p.destination}</span>
                    <span className="text-charcoal-400">{(p.block_reason || "").replace(/_/g, " ")}</span>
                  </div>
                ))}
              </div>
            </section>
          )}

          {components.length > 0 && (
            <section data-testid="detail-panel-breakdown">
              <p className="text-[10px] font-mono uppercase tracking-widest text-charcoal-500 mb-2">
                Opportunity score breakdown · {sb.total ?? 0}/100
              </p>
              <div className="space-y-1">
                {components.map((c, i) => (
                  <div key={i} className="flex items-baseline gap-2 text-[12px]">
                    <span className="inline-block w-9 text-right font-mono font-semibold text-charcoal-900">
                      +{c.points}
                    </span>
                    <div className="flex-1">
                      <div className="text-charcoal-900">{(c.signal || "").replace(/_/g, " ")}</div>
                      {c.evidence && (
                        <div className="text-charcoal-500 text-[11px] font-mono truncate" title={c.evidence}>
                          {c.evidence}
                        </div>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </section>
          )}

          <section data-testid="detail-panel-org">
            <div className="flex items-center justify-between mb-2">
              <p className="text-[10px] font-mono uppercase tracking-widest text-charcoal-500">
                Organization summary
              </p>
              {pqCls && (
                <span
                  data-testid="detail-panel-personalization-quality"
                  className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full border text-[10px] font-mono uppercase tracking-widest ${pqCls}`}
                >
                  Personalization: {pqLabel}
                </span>
              )}
            </div>
            <div className="space-y-2 text-sm">
              {evSum?.name && (
                <p><span className="text-charcoal-500">Name: </span><span className="text-charcoal-900">{evSum.name}</span></p>
              )}
              {evSum?.mission_summary && (
                <p className="text-charcoal-700"><span className="text-charcoal-500">Mission: </span>{evSum.mission_summary}</p>
              )}
              {evSum?.location && (
                <p><span className="text-charcoal-500">Location: </span><span className="text-charcoal-900">{evSum.location}</span></p>
              )}
              {(evSum?.programs || []).length > 0 && (
                <p><span className="text-charcoal-500">Programs: </span><span className="text-charcoal-900">{evSum.programs.join(" · ")}</span></p>
              )}
              {(evSum?.leadership || []).length > 0 && (
                <p><span className="text-charcoal-500">Leadership: </span><span className="text-charcoal-900">{evSum.leadership.join(" · ")}</span></p>
              )}
              {(evSum?.campaign_names || []).length > 0 && (
                <p><span className="text-charcoal-500">Campaigns: </span><span className="text-charcoal-900">{evSum.campaign_names.join(" · ")}</span></p>
              )}
              {(evSum?.platforms_detected || []).length > 0 && (
                <p><span className="text-charcoal-500">Donation platforms: </span><span className="text-charcoal-900 font-mono">{evSum.platforms_detected.join(", ")}</span></p>
              )}
            </div>
          </section>

          {evSum?.positive_observation && (
            <section data-testid="detail-panel-positives">
              <p className="text-[10px] font-mono uppercase tracking-widest text-charcoal-500 mb-2">
                Positive observations
              </p>
              <div className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full border border-emerald-200 bg-emerald-50 text-emerald-800 text-xs">
                <CheckCircle2 className="w-3 h-3" /> {evSum.positive_observation}
              </div>
            </section>
          )}
        </div>
      </div>
    </div>
  );
}

function Stat({ label, value }) {
  return (
    <div className="rounded-lg border border-black/5 bg-charcoal-50/40 px-3 py-2">
      <p className="text-[10px] font-mono uppercase tracking-widest text-charcoal-500">{label}</p>
      <p className="text-lg font-semibold text-charcoal-900 mt-0.5">{value}</p>
    </div>
  );
}


// Iter-54 — Prospect Scan History section
// ---------------------------------------------------------------------- //
function ProspectScanHistory({ batches, loading, onOpen, onDelete, currentBatchId }) {
  const [collapsed, setCollapsed] = useState(false);
  if (loading && batches.length === 0) {
    return (
      <div className="rounded-2xl bg-white border border-black/5 shadow-soft p-6 mb-6"
           data-testid="prospect-history-loading">
        <div className="flex items-center gap-2 text-charcoal-500">
          <Loader2 className="w-4 h-4 animate-spin" />
          <span className="text-sm">Loading batch history…</span>
        </div>
      </div>
    );
  }
  if (batches.length === 0) return null;

  return (
    <section className="rounded-2xl bg-white border border-black/5 shadow-soft p-6 mb-6"
             data-testid="prospect-history-section">
      <button
        type="button"
        onClick={() => setCollapsed(v => !v)}
        data-testid="prospect-history-toggle"
        className="w-full flex items-center justify-between gap-2 text-left"
      >
        <div className="flex items-center gap-2">
          <History className="w-4 h-4 text-charcoal-700" />
          <h2 className="text-lg font-semibold tracking-tight text-charcoal-900">
            Prospect Scan History
          </h2>
          <span className="text-[10.5px] font-mono uppercase tracking-widest text-charcoal-400 ml-1">
            {batches.length} {batches.length === 1 ? "batch" : "batches"}
          </span>
        </div>
        <span className="text-[10.5px] font-mono uppercase tracking-widest text-charcoal-500 hover:text-charcoal-900">
          {collapsed ? "Show" : "Hide"}
        </span>
      </button>

      {!collapsed && (
        <div className="mt-4 overflow-x-auto">
          <table className="w-full text-sm min-w-[820px]">
            <thead className="text-[10px] font-mono uppercase tracking-widest text-charcoal-500 border-b border-black/5">
              <tr>
                <th className="text-left py-2 pr-3">Started</th>
                <th className="text-left py-2 pr-3">Batch</th>
                <th className="text-left py-2 pr-3">Status</th>
                <th className="text-right py-2 pr-3">Total</th>
                <th className="text-right py-2 pr-3">Done</th>
                <th className="text-right py-2 pr-3">Failed</th>
                <th className="text-right py-2 pr-3">Cancelled</th>
                <th className="text-right py-2 pr-3 text-red-700">Critical</th>
                <th className="text-right py-2 pr-3 text-amber-700">High</th>
                <th className="text-right py-2 pr-3 text-emerald-700">Leads</th>
                <th className="text-right py-2 pr-3"></th>
              </tr>
            </thead>
            <tbody>
              {batches.map((b) => {
                const isCurrent = b.batch_id === currentBatchId;
                return (
                  <tr key={b.batch_id}
                      data-testid={`prospect-history-row-${b.batch_id}`}
                      className={`border-b border-black/5 hover:bg-charcoal-50/40 ${isCurrent ? "bg-blue-50/30" : ""}`}>
                    <td className="py-2.5 pr-3">
                      <div className="text-charcoal-900" title={fullDate(b.started_at)}>
                        {relativeDate(b.started_at)}
                      </div>
                      <div className="text-[10.5px] text-charcoal-400 font-mono">
                        {fullDate(b.started_at)}
                      </div>
                    </td>
                    <td className="py-2.5 pr-3">
                      <div className="text-charcoal-900 font-medium">{b.label}</div>
                      <div className="text-[10.5px] text-charcoal-400 font-mono uppercase tracking-widest">
                        {b.scan_mode === "full" ? "Full mode" : "Prospect mode"}
                      </div>
                    </td>
                    <td className="py-2.5 pr-3"><BatchStatusChip status={b.status} /></td>
                    <td className="py-2.5 pr-3 text-right font-mono">{b.total}</td>
                    <td className="py-2.5 pr-3 text-right font-mono text-emerald-700">{b.completed}</td>
                    <td className="py-2.5 pr-3 text-right font-mono text-red-700">
                      {((b.failed || 0) + (b.terminated || 0)) || <span className="text-charcoal-300">0</span>}
                    </td>
                    <td className="py-2.5 pr-3 text-right font-mono text-charcoal-500">
                      {b.cancelled || <span className="text-charcoal-300">0</span>}
                    </td>
                    <td className="py-2.5 pr-3 text-right font-mono">
                      {b.critical > 0
                        ? <span className="text-red-700 font-semibold">{b.critical}</span>
                        : <span className="text-charcoal-300">0</span>}
                    </td>
                    <td className="py-2.5 pr-3 text-right font-mono">
                      {b.high > 0
                        ? <span className="text-amber-700 font-semibold">{b.high}</span>
                        : <span className="text-charcoal-300">0</span>}
                    </td>
                    <td className="py-2.5 pr-3 text-right font-mono">
                      {b.leads_saved > 0
                        ? <span className="text-emerald-700 font-semibold">{b.leads_saved}</span>
                        : <span className="text-charcoal-300">0</span>}
                    </td>
                    <td className="py-2.5 pr-3 text-right">
                      <div className="inline-flex items-center gap-1.5">
                        <button
                          onClick={() => onOpen(b.batch_id)}
                          data-testid={`prospect-history-view-${b.batch_id}`}
                          className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full border border-black/10 bg-white text-[10.5px] font-mono uppercase tracking-widest text-charcoal-700 hover:border-black/20"
                        >
                          <Eye className="w-3 h-3" />
                          {b.status === "running" ? "Watch" : "View"}
                        </button>
                        {/* Iter-56c / Iter-66 — Delete action on
                            non-running batches. Backend also enforces
                            this (409 while any row is active). The
                            confirmation is a proper modal owned by
                            the parent (`onDelete` receives batch id +
                            label + total). */}
                        {b.status !== "running" && onDelete && (
                          <button
                            onClick={() => onDelete(b.batch_id, b.label, b.total)}
                            data-testid={`prospect-history-delete-${b.batch_id}`}
                            aria-label={`Delete batch ${b.label || b.batch_id}`}
                            title="Delete this batch"
                            className="inline-flex items-center justify-center w-7 h-7 rounded-full border border-black/10 bg-white text-charcoal-500 hover:text-red-700 hover:border-red-200 hover:bg-red-50 transition-colors"
                          >
                            <Trash2 className="w-3.5 h-3.5" />
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </section>
  );
}
